import React, { useEffect, useMemo, useState } from 'react';
/* global BigInt */
import { ADMIN_WALLET } from '../utils/constants';
import { apiFetch } from '../utils/serverConfig';
import './StoreOrder.css';
import { BrowserProvider, Contract } from 'ethers';

// Lightweight Pinata gateway config (client-side only). This avoids importing
// the Pinata SDK in the browser and keeps the gateway URL available for
// building metadata and GIF URLs. Replace with server-side calls for production.
const pinata = {
  pinataGateway: 'https://turquoise-many-mouse-901.mypinata.cloud/'
};

const TRANSLATIONS = {
  en: {
    purchaseHelpIntro: 'If you need to purchase Ethereum: Services like',
    purchaseHelpService: 'Venmo',
    purchaseHelpRest: 'let you buy crypto and (in many cases) send it to an external wallet such as your MetaMask address. Once the ETH is in your wallet you can use it to pay here. Be aware prices can move between purchase and payment — always confirm amounts before sending.',
    venmoHelpUrl: 'https://help.venmo.com/hc/en-us/search?q=crypto',
    venmoHelpLabel: '(Venmo help)'
  },
  es: {
    purchaseHelpIntro: 'Si necesitas comprar Ethereum: Servicios como',
    purchaseHelpService: 'Venmo',
    purchaseHelpRest: 'te permiten comprar criptomonedas y, en muchos casos, enviarlas a una billetera externa como MetaMask. Una vez que el ETH esté en tu billetera, puedes usarlo para pagar aquí. Ten en cuenta que los precios pueden cambiar entre la compra y el pago — confirma los montos antes de enviar.',
    venmoHelpUrl: 'https://help.venmo.com/hc/en-us/search?q=crypto',
    venmoHelpLabel: ''
  }
};

const getLocale = () => {
  try {
    const l = (navigator.language || 'en').split('-')[0];
    return TRANSLATIONS[l] ? l : 'en';
  } catch (e) {
    return 'en';
  }
};

const LOCALE = getLocale();

// Coaching product definition (reused when merging server products)
const COACHING_PRODUCT = {
  id: 'private_coaching',
  name: 'Private Coaching (per 30 min)',
  priceUsd: 40, // price per 30 minute block
  stock: 999,
  coaches: ['Dave', 'Stu', 'Jason'],
  durations: ['30', '60'] // 30 -> 1 block, 60 -> 2 blocks
};

// Purchase help component (renders localized text without innerHTML and shows a small hover tooltip)
function PurchaseHelp({ locale = 'en' }) {
  const [showTip, setShowTip] = useState(false);
  const t = TRANSLATIONS[locale] || TRANSLATIONS.en;
  const url = t.venmoHelpUrl;
  return (
    <div>
      <p style={{ marginTop: 6 }}>
        <strong>{t.purchaseHelpIntro}</strong>{' '}
        <a href={url} target="_blank" rel="noreferrer" style={{ color: '#7bdcff', textDecoration: 'underline' }}>{t.purchaseHelpService}</a>{' '}
        {t.purchaseHelpRest}
      </p>
      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <a href={url} target="_blank" rel="noreferrer" style={{ color: '#9fbbe6', fontSize: 13 }}>{t.venmoHelpLabel || 'Learn more'}</a>
        <span
          onMouseEnter={() => setShowTip(true)}
          onMouseLeave={() => setShowTip(false)}
          style={{ position: 'relative', display: 'inline-block', cursor: 'help', color: '#b8c2d1', fontSize: 13 }}
          aria-label="Venmo help tooltip">
          ⓘ
          {showTip && (
            <div style={{
              position: 'absolute',
              bottom: '120%',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'rgba(20,24,28,0.95)',
              color: '#fff',
              padding: '8px 10px',
              borderRadius: 8,
              fontSize: 12,
              whiteSpace: 'nowrap',
              boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
              zIndex: 9999
            }}>
              Opens Venmo help (crypto) in a new tab
            </div>
          )}
        </span>
      </div>
    </div>
  );
}

// Fallback: if for some reason the imported CSS didn't apply (dev server/hmr/service-worker
// caching issues), inject a minimal fallback so the Store still looks correct.
const STOREORDER_FALLBACK_CSS = `
/* fallback styles - minimal subset of StoreOrder.css (glassy upgrade) */
.store-wrap{padding:20px;max-width:940px;margin:18px auto;color:#e9eef5;font-family:Inter,system-ui,-apple-system,'Segoe UI',Roboto,Arial}
.store-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(260px,1fr));gap:16px;margin-top:18px}
.store-card{display:flex;justify-content:space-between;align-items:center;padding:16px;border-radius:16px;border:1px solid rgba(255,255,255,0.06);background:linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.008));backdrop-filter:blur(10px) saturate(140%);box-shadow:0 10px 30px rgba(11,15,30,0.6);transition:transform .18s ease,box-shadow .18s ease}
.store-card:hover{transform:translateY(-6px);box-shadow:0 18px 48px rgba(11,15,30,0.7)}
.highlighted-collectable{box-shadow:0 18px 60px rgba(124,58,237,0.32);transform:translateY(-8px);border:1px solid rgba(124,58,237,0.6)}
.store-controls{display:flex;align-items:center;gap:8px}
.store-actions{margin-top:14px;display:flex;gap:10px;flex-wrap:wrap}
.store-input{padding:10px;border-radius:12px;border:1px solid rgba(255,255,255,0.06);background:linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.01));color:#e9eef5;min-width:120px}
.store-input:focus{outline:none;box-shadow:0 6px 28px rgba(124,58,237,0.12);border-color:rgba(124,58,237,0.6)}
.store-btn{padding:10px 14px;border-radius:12px;background:linear-gradient(135deg,#7c3aed,#14f195);color:#fff;border:none;font-weight:700;cursor:pointer}
.store-btn.ghost{background:transparent;border:1px solid rgba(255,255,255,0.06)}
.coach-badge{padding:6px 10px;border-radius:999px;background:linear-gradient(90deg,#ffd966,#ffb84d);color:#3b2f00;font-weight:800}
.store-meta{font-size:12px;color:#aab8d6}
/* Mystery box animation */
.mystery-box{width:84px;height:84px;border-radius:12px;background:linear-gradient(180deg,#2b076e,#7c3aed);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;box-shadow:0 12px 40px rgba(124,58,237,0.28);position:relative;overflow:hidden}
.mystery-box:before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 20% 20%, rgba(255,255,255,0.08), transparent 10%), linear-gradient(120deg, rgba(255,255,255,0.02), rgba(255,255,255,0));transform:translateY(-8px);opacity:0.9}

mystery-shine{position:absolute;left:-60%;top:-40%;width:40%;height:200%;background:linear-gradient(120deg,rgba(255,255,255,0.06),rgba(255,255,255,0.18),rgba(255,255,255,0.06));transform:rotate(-20deg);animation:shine 2.6s linear infinite}
@keyframes shine{0%{left:-60%}50%{left:120%}100%{left:120%}}

/* Raffle / mystery-box special styling (red glow, ribbon, and badge) */
.raffle-card{position:relative;box-shadow:0 20px 60px rgba(255,54,54,0.08);border:1px solid rgba(255,54,54,0.12);animation:rafflePulse 3s ease-in-out infinite}
.raffle-card:after{content:'';position:absolute;inset:0;border-radius:16px;pointer-events:none;box-shadow:0 0 36px rgba(255,40,40,0.08) inset}
@keyframes rafflePulse{0%{transform:translateY(0)}50%{transform:translateY(-3px)}100%{transform:translateY(0)}}

.raffle-info{margin-top:8px;background:linear-gradient(180deg,rgba(255,10,10,0.03),rgba(255,10,10,0.01));padding:12px;border-radius:10px;border:1px solid rgba(255,40,40,0.06);color:#ffdede;font-size:14px}
.raffle-badge-row{display:flex;align-items:center}
.raffle-prize{font-weight:800;color:#ffd5d5}
.raffle-logic{font-size:13px;color:#f7c7c7}

/* Decorative box icon (CSS-only) */
.mystery-box{width:84px;height:84px;border-radius:12px;background:linear-gradient(180deg,#7b0026,#cf2947);display:flex;align-items:center;justify-content:center;color:#fff;font-weight:900;box-shadow:0 8px 40px rgba(255,20,60,0.12);position:relative;overflow:hidden}
.mystery-box:before{content:'';position:absolute;left:50%;top:50%;width:72%;height:72%;transform:translate(-50%,-50%) rotate(45deg);background:linear-gradient(180deg,rgba(255,255,255,0.06),rgba(0,0,0,0.06));border-radius:6px;opacity:0.9}
.mystery-ribbon{position:absolute;top:10px;left:50%;transform:translateX(-50%);width:64%;height:12px;background:linear-gradient(90deg,#ffdede,#ff6b6b);border-radius:6px;box-shadow:0 6px 18px rgba(255,80,80,0.12)}
.mystery-seal{position:absolute;bottom:8px;right:8px;background:rgba(0,0,0,0.18);padding:4px 8px;border-radius:8px;font-size:12px}

.raffle-card .store-meta{color:#ffdede}

/* Raffle summary hover (condensed) */
.raffle-summary{display:inline-block;position:relative;cursor:help}
.raffle-summary .raffle-tooltip{visibility:hidden;opacity:0;transition:opacity .18s ease,visibility .18s ease;position:absolute;left:0;top:28px;min-width:260px;background:rgba(10,8,10,0.92);color:#fff;padding:10px;border-radius:8px;border:1px solid rgba(255,255,255,0.04);box-shadow:0 8px 28px rgba(0,0,0,0.6);z-index:9999}
.raffle-summary:hover .raffle-tooltip{visibility:visible;opacity:1}

/* GIF styling to make it pop */
.raffle-gif{width:120px;height:120px;object-fit:cover;border-radius:12px;box-shadow:0 12px 48px rgba(255,80,90,0.14);transform-origin:center;animation:gifPulse 4s ease-in-out infinite}
@keyframes gifPulse{0%{transform:scale(1)}50%{transform:scale(1.06)}100%{transform:scale(1)}}
.raffle-gif.glow{filter:drop-shadow(0 8px 28px rgba(255,80,90,0.28)) saturate(140%)}

/* Admin overlay */
.admin-overlay{position:fixed;inset:0;background:rgba(2,6,12,0.6);display:flex;align-items:center;justify-content:center;z-index:10000}
.admin-panel{width:760px;max-width:95%;background:linear-gradient(180deg,#071020, #0f1724);padding:20px;border-radius:12px;border:1px solid rgba(255,255,255,0.04);box-shadow:0 30px 90px rgba(2,6,12,0.8);color:#e6f0ff}
.admin-panel h3{margin-top:0}
.admin-list{max-height:360px;overflow:auto;border:1px solid rgba(255,255,255,0.03);padding:10px;border-radius:8px;background:rgba(255,255,255,0.02)}
.admin-actions{display:flex;gap:8px;margin-top:12px}


`;



// Simple in-app store for new customers. No PIN or wallet required to browse.
// Products: Rashguard ($40), Shorts ($40), Gi / Uniform ($110).
// Option to pay with MetaMask (ETH conversion via CoinGecko) or pay USD (local fallback).

// Products will be loaded from server API (/api/products)

function fmtUsd(n) {
  return `$${Number(n).toFixed(2)}`;
}

// Styles are loaded from `StoreOrder.css` in the same folder.

const StoreOrder = () => {
  // cart is an array of items: { id, size, category, qty }
  // Normalize legacy shapes (object maps) into the array shape to avoid `cart.map` errors.
  const normalizeCart = (maybe) => {
    if (!maybe) return [];
    if (Array.isArray(maybe)) return maybe;
    // If it's a plain object, try to convert keys -> items
    if (typeof maybe === 'object') {
      try {
        const out = [];
        Object.keys(maybe).forEach(k => {
          const v = maybe[k];
          if (v == null) return;
          if (typeof v === 'number') {
            out.push({ id: k, qty: v, size: 'M', category: 'adult' });
          } else if (typeof v === 'object') {
            // already likely an item shape or legacy { qty, size }
            const qty = Number(v.qty || v.count || 1);
            const size = v.size || v.s || 'M';
            const category = v.category || v.cat || 'adult';
            out.push({ id: k, qty, size, category });
          }
        });
        return out;
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem('store_cart');
      const parsed = raw ? JSON.parse(raw) : [];
      return normalizeCart(parsed);
    } catch (e) {
      return [];
    }
  });

  // temporary selection per product before adding to cart
  const [selectedOptions, setSelectedOptions] = useState({});

  const [ethPriceUsd, setEthPriceUsd] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [products, setProducts] = useState([]);
  const [highlightedProductId, setHighlightedProductId] = useState(null);
  const [status, setStatus] = useState(null);
  const [txHash, setTxHash] = useState(null);
  const [showMetaMaskInfo, setShowMetaMaskInfo] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [claimStatuses, setClaimStatuses] = useState({});
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [buyerWallet, setBuyerWallet] = useState('');
  // Admin overlay state for raffle management
  const [showAdmin, setShowAdmin] = useState(false);
  const [raffleTickets, setRaffleTickets] = useState([]);
  const [raffleWinner, setRaffleWinner] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [recipientAddresses, setRecipientAddresses] = useState({});
  const [adminWalletSearch, setAdminWalletSearch] = useState('');
  const [serverOrders, setServerOrders] = useState([]);
  const [serverOrdersLoading, setServerOrdersLoading] = useState(false);
  // Promo modal state for /create?promo=blackfriday
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [promoFirstName, setPromoFirstName] = useState('');
  const [promoLastName, setPromoLastName] = useState('');
  const [promoContact, setPromoContact] = useState(''); // email or wallet
  const [promoProcessing, setPromoProcessing] = useState(false);
  const [showMobileCheckout, setShowMobileCheckout] = useState(false);
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [isMobileView, setIsMobileView] = useState(() => {
    try { return (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 640px)').matches) || false; } catch (e) { return false; }
  });


  useEffect(() => {
    localStorage.setItem('store_cart', JSON.stringify(cart));
  }, [cart]);

  // Load raffle tickets into state when admin overlay opens
  useEffect(() => {
    if (!showAdmin) return;
    try {
      const raw = localStorage.getItem('raffleTickets');
      const arr = raw ? JSON.parse(raw) : [];
      setRaffleTickets(arr);
    } catch (e) {
      setRaffleTickets([]);
    }
  }, [showAdmin]);

  // keep highlightIndex in range if tickets change
  useEffect(() => {
    if (raffleTickets.length === 0) setHighlightIndex(0);
    else setHighlightIndex(idx => Math.min(idx, raffleTickets.length - 1));
  }, [raffleTickets]);

  // runtime check: if imported CSS didn't apply (HMR/service-worker dev hiccup), inject fallback
  useEffect(() => {
    if (typeof document === 'undefined') return;
    try {
      const probe = document.createElement('div');
      probe.className = 'store-wrap';
      probe.style.display = 'none';
      document.body.appendChild(probe);
      const cs = getComputedStyle(probe);
      const colorOk = cs.color && (cs.color.indexOf('rgb') !== -1 || cs.color.indexOf('#') !== -1);
      document.body.removeChild(probe);
      if (!colorOk) {
        if (!document.getElementById('storeorder-fallback')) {
          const s = document.createElement('style');
          s.id = 'storeorder-fallback';
          s.textContent = STOREORDER_FALLBACK_CSS;
          document.head.appendChild(s);
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Listen for external requests to open/select a specific collectable/product
  useEffect(() => {
    const handler = (ev) => {
      try {
        const d = ev && ev.detail ? ev.detail : (ev && ev.data ? ev.data : null);
        if (!d) return;
        const order = d.order || d;
        if (!order) return;
        // prefer first item id from the order
        const items = Array.isArray(order.items) ? order.items : (order.items ? [order.items] : []);
        const first = items[0] || {};
        const itemId = first.id || first.productId || first.name || null;
        if (!itemId) return;
        // normalize id to product id in our products list if possible
        const found = products.find(p => String(p.id).toLowerCase() === String(itemId).toLowerCase() || String(p.name).toLowerCase().includes(String(itemId).toLowerCase()));
        const pid = found ? found.id : itemId;
        // highlight and scroll into view
        setHighlightedProductId(pid);
        try {
          const el = document.querySelector(`.store-product[data-product-id=\"${pid}\"]`);
          if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch (e) {}
        // clear highlight after a short period
        setTimeout(() => { try { setHighlightedProductId(null); } catch (e) {} }, 6000);
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('openCollectable', handler);
    // also support postMessage style where event.data may contain { order }
    window.addEventListener('message', (m) => {
      try { if (m && m.data && (m.data.order || m.data.type === 'openCollectable')) window.dispatchEvent(new CustomEvent('openCollectable', { detail: m.data.order || m.data })); } catch (e) {}
    });

    return () => {
      try { window.removeEventListener('openCollectable', handler); } catch (e) {}
    };
  }, [products]);

  // Auto-open /create?promo=blackfriday to show promo modal for quick share links
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const promo = params.get('promo') || params.get('bf') || null;
      const path = window.location.pathname || '';
      if ((path === '/create' || path === '/create/') && promo === 'blackfriday') {
        setShowPromoModal(true);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Detect MetaMask / in-app / webview browsers to toggle mobile-optimized UI
  useEffect(() => {
    try {
      const ua = (navigator.userAgent || '').toLowerCase();
      const isWebview = /wv|webview|instagram|fbav|fb_iab|twitter|puffin/i.test(ua);
      const isMetaMask = !!(window?.ethereum?.isMetaMask || window?.ethereum?.isTrust);
      const isInApp = isWebview || isMetaMask || /metamask/i.test(ua) || /metamask/i.test(navigator.userAgent || '');
      setIsInAppBrowser(!!isInApp);
      try {
        const root = document.querySelector('.store-wrap') || document.body;
        if (isInApp) {
          root.classList.add('inapp-browser');
          // also add a more specific metamask class when the UA or provider indicates MetaMask
          if (/metamask/i.test(navigator.userAgent || '') || (window?.ethereum?.isMetaMask || window?.ethereum?.isTrust)) {
            root.classList.add('metamask-browser');
            try { document.body.classList.add('metamask-browser'); } catch (e) {}
          }
          // if this is a small viewport, also mark mobile-optimized for easier CSS overrides
          try { if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 640px)').matches) root.classList.add('mobile-optimized'); } catch (e) {}
        } else {
          root.classList.remove('inapp-browser');
          root.classList.remove('metamask-browser');
          try { document.body.classList.remove('metamask-browser'); } catch (e) {}
          try { document.querySelector('.store-wrap') && document.querySelector('.store-wrap').classList.remove('mobile-optimized'); } catch (e) {}
        }
      } catch (e) {
        // ignore DOM tweaks
      }
    } catch (e) {
      // ignore detection errors
    }
  }, []);

  // Mobile viewport listener: toggle `mobile-optimized` class for compact, fast mobile UI
  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.matchMedia) return undefined;
      const mq = window.matchMedia('(max-width: 640px)');
      const onChange = (ev) => {
        const root = document.querySelector('.store-wrap') || document.body;
        setIsMobileView(!!ev.matches);
        try {
          if (ev.matches) root.classList.add('mobile-optimized'); else root.classList.remove('mobile-optimized');
        } catch (e) {}
      };
      // initialize
      onChange(mq);
      if (mq.addEventListener) mq.addEventListener('change', onChange);
      else mq.addListener(onChange);
      return () => { if (mq.removeEventListener) mq.removeEventListener('change', onChange); else mq.removeListener(onChange); };
    } catch (e) {
      return undefined;
    }
  }, []);

  // Poll briefly for late-injected wallet providers (MetaMask mobile sometimes injects after load)
  useEffect(() => {
    let mounted = true;
    let tries = 0;
    if (typeof window === 'undefined') return undefined;
    const poll = setInterval(() => {
      try {
        tries += 1;
        const hasEth = !!(window.ethereum);
        const uaHas = /metamask/i.test(navigator.userAgent || '');
        if (hasEth || uaHas) {
          if (!isInAppBrowser) setIsInAppBrowser(true);
          const root = document.querySelector('.store-wrap') || document.body;
          try { root.classList.add('inapp-browser'); } catch (e) {}
          try { root.classList.add('metamask-browser'); } catch (e) {}
          try { document.body.classList.add('metamask-browser'); } catch (e) {}
          if (mounted) clearInterval(poll);
        }
        if (tries >= 12) { // ~6 seconds at 500ms
          clearInterval(poll);
        }
      } catch (e) { if (mounted) clearInterval(poll); }
    }, 500);
    return () => { mounted = false; try { clearInterval(poll); } catch (e) {} };
  }, [isInAppBrowser]);

  // Lock body scroll when promo modal is open to avoid background scroll/jank in webviews
  useEffect(() => {
    try {
      if (showPromoModal) {
        document.body.classList.add('modal-open');
        // on some webviews, also set touch-action
        document.body.style.touchAction = 'manipulation';
      } else {
        document.body.classList.remove('modal-open');
        document.body.style.touchAction = '';
      }
    } catch (e) {}
    return () => {
      try { document.body.classList.remove('modal-open'); document.body.style.touchAction = ''; } catch (e) {}
    };
  }, [showPromoModal]);

  // Auto-open Black Friday view when URL contains ?bf=1 or #bf
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const isHash = window.location.hash === '#bf' || window.location.hash === '#blackfriday';
      if (params.get('bf') === '1' || isHash) {
        document.body.classList.add('bf-page-active');
        if (!document.querySelector('.bf-page-bg')) {
          const bg = document.createElement('div');
          bg.className = 'bf-page-bg';
          bg.setAttribute('aria-hidden', 'true');
          document.body.appendChild(bg);
        }
      }
    } catch (e) {
      // ignore
    }
  }, []);

  // Styles are loaded from `StoreOrder.css`

  // Fetch ETH price (USD) from CoinGecko to convert USD -> ETH
  useEffect(() => {
    let mounted = true;
    setLoadingPrice(true);
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd')
      .then((r) => r.json())
      .then((json) => {
        if (!mounted) return;
        const val = json?.ethereum?.usd;
        if (val) setEthPriceUsd(Number(val));
      })
      .catch(() => {
        // ignore
      })
      .finally(() => mounted && setLoadingPrice(false));
    return () => {
      mounted = false;
    };
  }, []);

  // Fetch products from API
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await apiFetch('/api/products');
        const json = await res.json();
        if (!mounted) return;
        if (Array.isArray(json.products)) {
          // ensure coaching product exists in the list
          const serverProducts = json.products || [];
          const found = serverProducts.find(p => p.id === COACHING_PRODUCT.id);
          /*
            Black Friday raffle product: customers buy the 'Mysterious Box' raffle ticket (priceUsd $20).
            Prize: $199 value item. Eligibility: requires buying at least `minItemsRequired` qualifying items
            (rashguard, T-Shirt, private coaching, or gi) in the same order to receive ticket entries.
            Tickets are currently issued client-side and stored in `localStorage.raffleTickets`. For on-chain
            NFT tickets, integrate server-side minting and add `ipfs` metadata to the product.
          */
          const foundRaffle = serverProducts.find(p => p.id === 'blackfriday_raffle');
          if (!found) serverProducts.push(COACHING_PRODUCT);
          if (!foundRaffle) serverProducts.push({ id: 'blackfriday_raffle', name: 'Black Friday — Mysterious Box (Raffle)', priceUsd: 20, stock: 999, category: 'raffle', prizeUsd: 199, minItemsRequired: 3, isMystery: true, ipfs: { ipfsHash: 'bafybeietsxweg65mhwoe25bpc3k3tkpb5gm7e63hbc32izv4e3azpi5hhe', gatewayUrl: `${pinata.pinataGateway}ipfs/bafybeietsxweg65mhwoe25bpc3k3tkpb5gm7e63hbc32izv4e3azpi5hhe` }, gifUrl: `${pinata.pinataGateway}ipfs/bafybeietsxweg65mhwoe25bpc3k3tkpb5gm7e63hbc32izv4e3azpi5hhe/mystery.gif` });
          setProducts(serverProducts);
        }
      } catch (err) {
        // fallback to built-in products if API unavailable
        if (!mounted) return;
        setProducts([
          { id: 'rashguard', name: 'Rashguard', priceUsd: 40, stock: 20 },
          { id: 'tshirt', name: 'T-Shirt', priceUsd: 30, stock: 30 },
          { id: 'shorts', name: 'Shorts', priceUsd: 40, stock: 20 },
          { id: 'gi', name: 'Uniform (Gi)', priceUsd: 110, stock: 10 },
          // Black Friday raffle product: mysterious box raffle
          { id: 'blackfriday_raffle', name: 'Black Friday — Mysterious Box (Raffle)', priceUsd: 20, stock: 999, category: 'raffle', prizeUsd: 199, minItemsRequired: 3, isMystery: true, ipfs: { ipfsHash: 'bafybeietsxweg65mhwoe25bpc3k3tkpb5gm7e63hbc32izv4e3azpi5hhe', gatewayUrl: `${pinata.pinataGateway}ipfs/bafybeietsxweg65mhwoe25bpc3k3tkpb5gm7e63hbc32izv4e3azpi5hhe` }, gifUrl: `${pinata.pinataGateway}ipfs/bafybeietsxweg65mhwoe25bpc3k3tkpb5gm7e63hbc32izv4e3azpi5hhe/mystery.gif` },
          COACHING_PRODUCT
        ]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const add = (id) => {
    setCart((c) => {
      const opt = selectedOptions[id] || {};
      // size for coaching product is duration ('30' or '60')
      const prod = products.find(p => p.id === id) || {};
      const size = opt.size || (prod.id === 'private_coaching' ? '30' : (prod.defaultSize || 'M'));
      const category = opt.category || 'adult';
      let coach = opt.coach || null;
      if (!coach && prod.coaches && prod.coaches.length > 0) {
        coach = prod.coaches[0];
      }
      // compute unit blocks: 30min -> 1, 60min -> 2
      const unitBlocks = (id === 'private_coaching' && String(size) === '60') ? 2 : 1;
      const next = [...c];
      const idx = next.findIndex(i => i.id === id && i.size === size && i.category === category && (i.coach || null) === coach);
      if (idx >= 0) {
        next[idx] = { ...next[idx], qty: next[idx].qty + unitBlocks };
      } else {
        next.push({ id, size, category, qty: unitBlocks, coach });
      }
      return next;
    });
  };

  const remove = (id) => {
    setCart((c) => {
      const opt = selectedOptions[id] || {};
      const prod = products.find(p => p.id === id) || {};
      const size = opt.size || (prod.id === 'private_coaching' ? '30' : (prod.defaultSize || 'M'));
      const category = opt.category || 'adult';
      let coach = opt.coach || null;
      if (!coach && prod.coaches && prod.coaches.length > 0) coach = prod.coaches[0];
      const unitBlocks = (id === 'private_coaching' && String(size) === '60') ? 2 : 1;
      const next = [...c];
      const idx = next.findIndex(i => i.id === id && i.size === size && i.category === category && (i.coach || null) === coach);
      if (idx === -1) return next;
      if (next[idx].qty > unitBlocks) next[idx] = { ...next[idx], qty: next[idx].qty - unitBlocks };
      else next.splice(idx, 1);
      return next;
    });
  };

  // derive items from cart grouped by product id+size+category
  const items = useMemo(() => {
    return cart.map(ci => {
      const p = products.find(pp => pp.id === ci.id) || { name: ci.id, priceUsd: 0 };
      return { id: ci.id, name: p.name, priceUsd: p.priceUsd ?? p.price ?? 0, qty: ci.qty, size: ci.size, category: ci.category, coach: ci.coach || null };
    });
  }, [cart, products]);

  const totalUsd = useMemo(() => items.reduce((s, it) => s + it.priceUsd * it.qty, 0), [items]);

  const totalEth = useMemo(() => {
    if (!ethPriceUsd || ethPriceUsd === 0) return null;
    return Number((totalUsd / ethPriceUsd).toFixed(6));
  }, [totalUsd, ethPriceUsd]);

  const detectMetaMask = () => !!(window?.ethereum?.isMetaMask);

  // default: treat as ETH payment converted from USD (we always convert USD -> ETH before sending)
  async function payWithMetaMask() {
    await payWithMetaMaskInternal({ payInUsd: false });
  }

  // payInUsd flag will be recorded in the receipt/meta; function always converts USD->ETH and sends ETH.
  async function payWithMetaMaskUsd() {
    await payWithMetaMaskInternal({ payInUsd: true });
  }

  async function payWithMetaMaskInternal({ payInUsd = false } = {}) {
    setStatus('connecting');
    setTxHash(null);
    try {
      if (!detectMetaMask()) {
        setStatus('no-metamask');
        return;
      }
      if (!totalEth || totalEth <= 0) {
        setStatus('empty-cart');
        return;
      }
      if (!firstName.trim() || !lastName.trim()) {
        setStatus('missing-name');
        return;
      }
      // Request account access (this prompts MetaMask if needed)
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const from = accounts && accounts[0];
      if (!from) {
        setStatus('no-account');
        return;
      }
      // remember buyer wallet for order/ticket persistence
      try { setBuyerWallet(from); } catch (e) {}

      // Build hex value for eth_sendTransaction without importing ethers utils
      // Convert decimal ETH to wei (BigInt) and return hex string
      const ethToWeiHex = (amt) => {
        const s = String(amt || '0');
        const [intPart, fracPart = ''] = s.split('.');
        const intBig = BigInt(intPart || '0') * 10n ** 18n;
        const fracPadded = (fracPart + '0'.repeat(18)).slice(0, 18);
        const fracBig = BigInt(fracPadded || '0');
        const wei = intBig + fracBig;
        return '0x' + wei.toString(16);
      };
      const valueHex = ethToWeiHex(totalEth);

      const txParams = {
        from,
        to: ADMIN_WALLET,
        value: valueHex,
        data: '0x' // no extra data
      };

      setStatus('sending');
      // Use window.ethereum to trigger MetaMask confirmation reliably
      const txHashResp = await window.ethereum.request({ method: 'eth_sendTransaction', params: [txParams] });
      setStatus('pending');
      setTxHash(txHashResp);

      // Build receipt/meta
      const orderMeta = {
        txHash: txHashResp,
        payInUsd: !!payInUsd,
        usdAmount: Number(totalUsd),
        ethAmount: Number(totalEth)
      };

      // Persist order (server + local) immediately after tx hash returned
      try {
        const resp = await apiFetch('/api/orders', {
          method: 'POST',
          body: JSON.stringify({
            buyer: { firstName: firstName.trim(), lastName: lastName.trim(), from },
            items: items.map(it => ({ id: it.id, name: it.name, qty: it.qty, price: it.priceUsd, size: it.size, category: it.category })),
            total: totalUsd,
            method: 'metamask',
            meta: orderMeta
          })
        });
        // If server created an order we can capture its id and use it for local persistence
        var serverOrderJson = null;
        try { serverOrderJson = await resp.json(); } catch (e) { serverOrderJson = null; }
      } catch (err) {
        console.warn('Order POST failed, will persist locally', err);
      }
      const savedOrder = { method: 'metamask', totalUsd, totalEth, txHash: txHashResp, items, buyer: { firstName: firstName.trim(), lastName: lastName.trim(), from }, meta: orderMeta };
      // If server returned an order object use its id so ControlCenter/server can reconcile
      if (serverOrderJson && serverOrderJson.order && serverOrderJson.order.id) {
        savedOrder.id = serverOrderJson.order.id;
      }
      persistOrder(savedOrder);
      // Process raffle tickets (if any) and attach to receipt for user visibility
      const raffleCreated = processRaffleTickets(savedOrder);
      const receiptObj = { id: `receipt_${Date.now()}`, createdAt: new Date().toISOString(), ...savedOrder };
      if (raffleCreated && raffleCreated.length) receiptObj.raffleTickets = raffleCreated;
      setReceipt(receiptObj);

      // clear cart and form
      setCart([]);
      setFirstName('');
      setLastName('');
      setStatus('success');
    } catch (e) {
      console.error('MetaMask payment failed', e);
      setStatus('failed');
    }
  }

  function persistOrder(order) {
    try {
      const raw = localStorage.getItem('store_orders');
      const arr = raw ? JSON.parse(raw) : [];
      const id = order.id || `order_${Date.now()}`;
      const toSave = { id, created: new Date().toISOString(), ...order };
      // Normalize buyer wallet/address to lowercase for reliable searching
      try {
        if (toSave && toSave.buyer) {
          const candidate = toSave.buyer.from || toSave.buyer.contact || toSave.buyer.address || null;
          if (candidate && typeof candidate === 'string') {
            const norm = candidate.trim().toLowerCase();
            toSave.buyer.from = norm;
            toSave.buyer.normalized = norm;
          }
        }
      } catch (e) {
        // ignore normalization failures
      }
      arr.push(toSave);
      localStorage.setItem('store_orders', JSON.stringify(arr));

      // Emit an in-window event so ControlCenter (or any listener) can react
      try { window.dispatchEvent(new CustomEvent('store:orderCreated', { detail: toSave })); } catch (e) {}

      // Best-effort: if server API is available and this order wasn't already created
      // server-side, attempt to create it and then request a server-side mint if applicable.
      (async () => {
        try {
          // If order already has an id that looks server-provided we skip create
          const needsCreate = !(order && order.id && String(order.id).startsWith('order_'));
          let serverOrder = null;
          if (needsCreate) {
            try {
              const resp = await apiFetch('/api/orders', { method: 'POST', body: JSON.stringify(order) });
              serverOrder = await resp.json().catch(() => null);
              if (serverOrder && serverOrder.order && serverOrder.order.id) {
                // update local store_orders entry with canonical id
                try {
                  const cur = JSON.parse(localStorage.getItem('store_orders') || '[]');
                  const idx = cur.findIndex(o => o.id === id);
                  if (idx >= 0) {
                    cur[idx] = { ...cur[idx], ...serverOrder.order };
                    localStorage.setItem('store_orders', JSON.stringify(cur));
                    // re-emit event with server order
                    try { window.dispatchEvent(new CustomEvent('store:orderCreated', { detail: cur[idx] })); } catch (e) {}
                  }
                } catch (e) {}
              }
            } catch (e) {
              // server create failed — that's fine; local persist remains
            }
          }

          // If we have a server-provided order id, and items that look like NFT products, try server mint
          const finalOrder = serverOrder && serverOrder.order ? serverOrder.order : toSave;
          if (finalOrder && finalOrder.id && finalOrder.items && Array.isArray(finalOrder.items)) {
            for (const it of finalOrder.items) {
              try {
                // only attempt server mint if a recipient wallet is available
                const toAddr = (finalOrder.buyer && (finalOrder.buyer.from || finalOrder.buyer.contact)) || (buyerWallet || null);
                if (!toAddr) continue;
                // try server mint endpoint for this order/item
                const mintResp = await apiFetch(`/api/orders/${encodeURIComponent(finalOrder.id)}/mint`, { method: 'POST', body: JSON.stringify({ itemId: it.id, toAddress: toAddr }) });
                if (mintResp && mintResp.ok) {
                  // done — server should refresh order status; we can fire an event to notify listeners
                  try { window.dispatchEvent(new CustomEvent('store:orderMinted', { detail: { orderId: finalOrder.id, itemId: it.id } })); } catch (e) {}
                }
              } catch (err) {
                // ignore per-item mint failures; client/admin can handle later
              }
            }
          }
        } catch (err) {
          // swallow best-effort sync errors
        }
      })();
    } catch (e) {
      // ignore
    }
  }

  // Process raffle tickets for orders that include the Black Friday raffle product.
  // Rules: customer must have purchased at least `minItemsRequired` qualifying items
  // (rashguard, tshirt, private coaching, gi) in the same order to be eligible.
  // When eligible, create token entries saved to `localStorage.raffleTickets` for later drawing.
  function processRaffleTickets(order) {
    try {
      if (!order || !order.items || !Array.isArray(order.items)) return [];
      const raffleItems = order.items.filter(it => it.id === 'blackfriday_raffle');
      if (!raffleItems.length) return [];
      const prod = products.find(p => p.id === 'blackfriday_raffle') || {};
      const minReq = prod.minItemsRequired || 3;
      const eligibleIds = ['rashguard', 'tshirt', 'private_coaching', 'gi'];
      const eligibleCount = order.items.filter(it => eligibleIds.includes(it.id)).reduce((s, it) => s + (it.qty || 0), 0);
      if (eligibleCount < minReq) {
        setStatus('raffle-ineligible');
        return [];
      }

      const raw = localStorage.getItem('raffleTickets');
      const arr = raw ? JSON.parse(raw) : [];
      const created = [];
      const orderRef = order.id || order.txHash || `local_${Date.now()}`;
      for (const r of raffleItems) {
        const qty = Number(r.qty || 1);
        for (let i = 0; i < qty; i++) {
          const tokenId = `raffle_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
          const t = { tokenId, orderRef, owner: `${order.buyer?.firstName || ''} ${order.buyer?.lastName || ''}`.trim(), ownerWallet: (order.buyer?.from || order.meta?.from || ''), createdAt: new Date().toISOString(), prizeUsd: prod.prizeUsd || 199, status: 'pending' };
          arr.push(t);
          created.push(t);
        }
      }
      localStorage.setItem('raffleTickets', JSON.stringify(arr));
      setStatus('raffle-tickets-created');
      return created;
    } catch (e) {
      console.warn('processRaffleTickets failed', e);
      return [];
    }
  }

  // Helper: copy text to clipboard and show a tiny fallback message
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (e) {
      // fallback: create a temp input
      try {
        const input = document.createElement('input');
        input.value = text;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        return true;
      } catch (_) {
        return false;
      }
    }
  };

  // Admin helpers: draw a random winner from localStorage.raffleTickets
  const drawRaffleWinner = () => {
    try {
      const raw = localStorage.getItem('raffleTickets');
      const arr = raw ? JSON.parse(raw) : [];
      if (!arr.length) {
        setRaffleWinner(null);
        return null;
      }
      const idx = Math.floor(Math.random() * arr.length);
      const winner = arr[idx];
      // persist winners list
      const winnersRaw = localStorage.getItem('raffleWinners');
      const winners = winnersRaw ? JSON.parse(winnersRaw) : [];
      const w = { ...winner, drawnAt: new Date().toISOString() };
      winners.push(w);
      localStorage.setItem('raffleWinners', JSON.stringify(winners));
      setRaffleWinner(w);
      return w;
    } catch (e) {
      console.warn('drawRaffleWinner failed', e);
      return null;
    }
  };

  // Persist a specific ticket as the drawn winner (useful to match animated selection)
  const drawRaffleWinnerAt = (index) => {
    try {
      const raw = localStorage.getItem('raffleTickets');
      const arr = raw ? JSON.parse(raw) : [];
      if (!arr.length || index < 0 || index >= arr.length) {
        setRaffleWinner(null);
        return null;
      }
      const winner = arr[index];
      const winnersRaw = localStorage.getItem('raffleWinners');
      const winners = winnersRaw ? JSON.parse(winnersRaw) : [];
      const w = { ...winner, drawnAt: new Date().toISOString() };
      winners.push(w);
      localStorage.setItem('raffleWinners', JSON.stringify(winners));
      setRaffleWinner(w);
      return w;
    } catch (e) {
      console.warn('drawRaffleWinnerAt failed', e);
      return null;
    }
  };

  const exportRaffleTickets = () => {
    try {
      const raw = localStorage.getItem('raffleTickets') || '[]';
      const blob = new Blob([raw], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `raffleTickets_${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // ignore
    }
  };

  // Admin: animate a roulette-style selection and persist the selected ticket
  const spinAndDraw = async () => {
    try {
      const raw = localStorage.getItem('raffleTickets');
      const arr = raw ? JSON.parse(raw) : [];
      if (!arr.length) return null;
      setIsSpinning(true);
      const cycles = 30 + Math.floor(Math.random() * 20); // variable cycles
      let current = 0;
      for (let i = 0; i < cycles; i++) {
        // accelerate then decelerate
        const pace = 10 + Math.floor((i / cycles) * 120);
        await new Promise(res => setTimeout(res, pace));
        current = (current + 1) % arr.length;
        setHighlightIndex(current);
      }
      // small final slowdowns
      for (let j = 0; j < 6; j++) {
        await new Promise(res => setTimeout(res, 200 + j * 80));
        current = (current + 1) % arr.length;
        setHighlightIndex(current);
      }
      // selected index
      const selected = current % arr.length;
      const winner = drawRaffleWinnerAt(selected);
      // refresh ticket list from storage to reflect potential updates
      try { const latest = JSON.parse(localStorage.getItem('raffleTickets') || '[]'); setRaffleTickets(latest); } catch (_) {}
      setIsSpinning(false);
      return winner;
    } catch (e) {
      console.warn('spinAndDraw failed', e);
      setIsSpinning(false);
      return null;
    }
  };

  // Admin: try to mint an NFT for the given ticket (uses client wallet mint flow)
  const adminMintWinnerNFT = async (ticket) => {
    if (!ticket) return;
    // construct a fake receipt/item for clientMint to use product metadata
    const product = products.find(p => p.id === 'blackfriday_raffle');
    if (!product) {
      alert('No raffle product metadata available. Cannot mint.');
      return;
    }
    const fakeReceipt = { id: `raffle_${ticket.tokenId}`, items: [{ id: 'blackfriday_raffle', name: product.name }], totalUsd: product.priceUsd || 20 };
    const fakeItem = { id: 'blackfriday_raffle' };
    try {
      // clientMint will attempt ERC minting via wallet; ensure MetaMask present
      await clientMint(fakeReceipt, fakeItem);
      // update ticket status locally
      try {
        const raw = localStorage.getItem('raffleTickets');
        const arr = raw ? JSON.parse(raw) : [];
        const idx = arr.findIndex(r => r.tokenId === ticket.tokenId);
        if (idx >= 0) {
          arr[idx].status = 'minted';
          arr[idx].mintedAt = new Date().toISOString();
          localStorage.setItem('raffleTickets', JSON.stringify(arr));
          setRaffleTickets(arr);
        }
      } catch (_) {}
    } catch (e) {
      console.warn('adminMintWinnerNFT failed', e);
      alert('Mint attempt failed — check console for details.');
    }
  };

  // Admin: mint to an explicit recipient address using the connected wallet (tries multiple contract methods)
  const adminMintToAddress = async (ticket, toAddress) => {
    if (!ticket) return;
    if (!toAddress || !toAddress.match(/^0x[0-9a-fA-F]{40}$/)) {
      alert('Please enter a valid recipient address (0x...).');
      return;
    }
    const product = products.find(p => p.id === 'blackfriday_raffle');
    if (!product) {
      alert('No raffle product metadata available. Cannot mint.');
      return;
    }

    // tokenURI / metadata for product
    const tokenURI = product?.ipfs?.gatewayUrl || (product?.ipfs?.ipfsHash ? `https://gateway.pinata.cloud/ipfs/${product.ipfs.ipfsHash}` : null);
    if (!tokenURI) {
      alert('No metadata URI available for product. Cannot mint.');
      return;
    }

    try {
      const ci = await apiFetch('/api/contract-info');
      const ciJson = await ci.json().catch(() => ({}));
      const address = ciJson.address || product?.contractAddress || null;
      const abi = ciJson.abi || product?.contractAbi || null;
      if (!address || !abi) {
        alert('Contract address/ABI not available on server or product. Cannot mint from admin console.');
        return;
      }

      if (!detectMetaMask()) {
        alert('MetaMask not detected — please open this page where MetaMask is available to mint.');
        return;
      }

      const provider = new BrowserProvider(window.ethereum);
      await provider.send('eth_requestAccounts', []);
      const signer = await provider.getSigner();
      const contract = new Contract(address, abi, signer);

      const tryMethods = [
        { name: 'mintTo', args: (to, uri) => [to, uri] },
        { name: 'safeMint', args: (to, uri) => [to, uri] },
        { name: 'mint', args: (to, uri) => [to, uri] },
        { name: 'mintToAddress', args: (to, uri) => [to, uri] },
        { name: 'mintWithTokenURI', args: (to, uri) => [to, uri] },
      ];

      for (const m of tryMethods) {
        if (typeof contract[m.name] !== 'function') continue;
        try {
          const args = m.args(toAddress, tokenURI);
          const tx = await contract[m.name](...args);
          const txHash = tx.hash || (tx.transactionHash || null);
          try {
            await tx.wait();
          } catch (waitErr) {
            // still continue to record txHash if present
          }

          // mark ticket as minted
          try {
            const raw = localStorage.getItem('raffleTickets');
            const arr = raw ? JSON.parse(raw) : [];
            const idx = arr.findIndex(r => r.tokenId === ticket.tokenId);
            if (idx >= 0) {
              arr[idx].status = 'minted';
              arr[idx].mintedAt = new Date().toISOString();
              arr[idx].mintedTo = toAddress;
              if (txHash) arr[idx].mintedTxHash = txHash;
              localStorage.setItem('raffleTickets', JSON.stringify(arr));
              setRaffleTickets(arr);
            }
          } catch (_) {}

          alert('Mint transaction submitted' + (txHash ? `: ${txHash}` : ''));
          return;
        } catch (err) {
          // try next method
          continue;
        }
      }

      alert('No compatible mint method found on contract.');
    } catch (err) {
      console.error('adminMintToAddress failed', err);
      alert('Mint failed: ' + String(err?.message || err));
    }
  };

  // Open a centered popup window with a small admin UI that manages raffle tickets.
  // The popup reads/writes localStorage directly so it works even if the opener is closed.
  const openAdminPopup = () => {
    try {
      const w = 900; const h = 720;
      const left = Math.max(0, Math.floor((window.screen.width - w) / 2));
      const top = Math.max(0, Math.floor((window.screen.height - h) / 2));
      const features = `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`;
      const popup = window.open('', 'AdminRafflePopup', features);
      if (!popup) { alert('Popup blocked — allow popups for this site to open the admin window.'); return; }

      // Basic styles for the popup (kept small and self-contained)
      const style = `
        :root{--glass-1:rgba(255,255,255,0.06);--glass-2:rgba(255,255,255,0.02);--accent:#ff5b6b;--muted:#9fbbe6}
        html,body{height:100%;margin:0;background:linear-gradient(135deg,#041022 0%,#081426 100%);font-family:Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial;color:#e6f0ff}
        .panel{width:100%;height:100%;display:flex;flex-direction:column;gap:14px;padding:18px;box-sizing:border-box}
        .top{display:flex;justify-content:space-between;align-items:center}
        .glass{background:linear-gradient(180deg,var(--glass-1),var(--glass-2));backdrop-filter:blur(14px) saturate(140%);-webkit-backdrop-filter:blur(14px) saturate(140%);border:1px solid rgba(255,255,255,0.06);border-radius:14px;padding:12px}
        .title{font-size:20px;font-weight:800}
        .meta{color:var(--muted)}
        .roulette{display:flex;align-items:center;justify-content:center;padding:18px}
        .wheel{display:flex;gap:10px;align-items:center;justify-content:center;flex-wrap:nowrap;overflow:hidden;width:100%;}
        .chip{min-width:110px;height:72px;border-radius:12px;background:linear-gradient(180deg,rgba(255,255,255,0.02),rgba(255,255,255,0.01));display:flex;flex-direction:column;align-items:center;justify-content:center;padding:8px;box-shadow:0 8px 30px rgba(0,0,0,0.6);border:1px solid rgba(255,255,255,0.04);transition:transform .16s ease,box-shadow .16s ease}
        .chip.highlight{transform:scale(1.08);box-shadow:0 20px 48px rgba(255,40,80,0.18);border:1px solid rgba(255,90,100,0.22)}
        .chip .id{font-weight:800;color:#fff}
        .chip .owner{font-size:12px;color:var(--muted);margin-top:4px}
        .controls{display:flex;gap:8px;align-items:center}
        .btn{padding:10px 14px;border-radius:10px;border:none;background:linear-gradient(90deg,#ff6b7b,#ff2d55);color:#fff;font-weight:700;cursor:pointer}
        .btn.ghost{background:transparent;border:1px solid rgba(255,255,255,0.06)}
        .footer{display:flex;justify-content:space-between;align-items:center}
        .list{max-height:200px;overflow:auto;margin-top:8px;padding:8px;border-radius:8px;background:rgba(0,0,0,0.06)}
        .ticket{padding:8px;border-bottom:1px solid rgba(255,255,255,0.02);display:flex;justify-content:space-between;align-items:center}
        .status{color:var(--muted)}
      `;

      const html = `
        <html><head><meta charset="utf-8"><title>Raffle Admin</title><meta name="viewport" content="width=device-width,initial-scale=1"><style>${style}</style></head>
        <body>
          <div class="panel">
            <div class="top">
              <div>
                <div class="title">Raffle Admin</div>
                <div class="meta">Manage raffle tickets — spin to draw a winner</div>
              </div>
              <div style="display:flex;gap:8px;align-items:center">
                <button id="exportBtn" class="btn ghost">Export</button>
                <button id="clearBtn" class="btn ghost">Clear</button>
                <button id="loadOrdersBtn" class="btn">Load Orders</button>
                <button id="closeBtn" class="btn">Close</button>
              </div>
            </div>

            <div class="glass roulette">
              <div style="flex:1;max-width:1100px">
                <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
                  <div>Tickets stored: <strong id="count">0</strong></div>
                  <div style="display:flex;gap:8px;align-items:center">
                    <div class="controls"><button id="spinBtn" class="btn">Spin & Draw</button></div>
                    <div style="margin-left:12px"><button id="checkTxBtn" class="btn ghost">Check Tx</button></div>
                  </div>
                </div>
                <div class="wheel" id="wheel" aria-live="polite" aria-atomic="true">
                  <!-- chips injected here -->
                </div>
              </div>
            </div>

            <div class="glass" style="display:flex;gap:12px;align-items:flex-start">
              <div style="flex:1">
                <div style="font-weight:700;margin-bottom:8px">Tickets</div>
                <div id="list" class="list"><div id="empty" style="color:#cbd5e1;padding:8px">No tickets found.</div></div>
              </div>
              <div style="width:280px">
                <div style="font-weight:700;margin-bottom:8px">Winner</div>
                <div id="winner" class="status">No winner yet.</div>
                <div style="margin-top:12px;display:flex;gap:8px">
                  <button id="mintBtn" class="btn ghost">Mint Winner (Main App)</button>
                  <button id="copyBtn" class="btn ghost">Copy Winner</button>
                </div>
              </div>
            </div>
            <div style="margin-top:12px" class="glass">
              <div style="font-weight:700;margin-bottom:8px">Server Orders</div>
              <div id="ordersList" class="list" style="max-height:180px;overflow:auto;padding:8px;border-radius:8px;background:rgba(0,0,0,0.04)">No orders loaded.</div>
            </div>
          </div>

          <script>
            (function(){
              const $ = id => document.getElementById(id);
              function makeChip(t,i){
                const d = document.createElement('div'); d.className='chip'; d.setAttribute('data-idx',i);
                const id = document.createElement('div'); id.className='id'; id.textContent = t.tokenId;
                const owner = document.createElement('div'); owner.className='owner'; owner.textContent = t.owner || 'Unknown';
                d.appendChild(id); d.appendChild(owner);
                return d;
              }
              function loadTickets(){
                try{
                  const raw = localStorage.getItem('raffleTickets')||'[]'; const arr = JSON.parse(raw);
                  $('count').textContent = (arr && arr.length) ? arr.length : 0;
                  const list = $('list'); list.innerHTML = '';
                  const wheel = $('wheel'); wheel.innerHTML = '';
                  if(!arr||!arr.length){ list.innerHTML = '<div id="empty" style="color:#cbd5e1;padding:8px">No tickets found.</div>'; $('winner').textContent='No winner yet.'; return; }
                  arr.forEach((t,i)=>{
                    const ticketDiv = document.createElement('div'); ticketDiv.className='ticket';
                    // numbered participant + owner + orderRef
                    ticketDiv.innerHTML = '<div><strong>#'+(i+1)+' — '+t.tokenId+'</strong><div style="font-size:12px;color:#9fbbe6">'+(t.owner||'Unknown')+' — '+(t.orderRef||'')+'</div></div>';
                    const actions = document.createElement('div'); actions.style.display='flex'; actions.style.gap='8px'; actions.style.alignItems='center';
                    const addr = document.createElement('input'); addr.type='text'; addr.placeholder='recipient 0x...'; addr.style.padding='6px'; addr.style.borderRadius='6px'; addr.style.border='1px solid rgba(255,255,255,0.04)'; addr.style.background='transparent'; addr.id = 'addr_'+i;
                    const mintBtn = document.createElement('button'); mintBtn.className='btn ghost'; mintBtn.textContent='Send NFT'; mintBtn.onclick = ()=>{
                      const to = document.getElementById('addr_'+i).value || null;
                      if (to) {
                        window.opener && window.opener.postMessage({ type:'mint-ticket', tokenId: t.tokenId, to }, '*');
                      } else {
                        // if no address provided, request mint to connected wallet
                        window.opener && window.opener.postMessage({ type:'mint-ticket', tokenId: t.tokenId }, '*');
                      }
                    };
                    actions.appendChild(addr);
                    actions.appendChild(mintBtn);
                    ticketDiv.appendChild(actions);
                    list.appendChild(ticketDiv);
                    // wheel chip
                    const chip = makeChip(t,i);
                    wheel.appendChild(chip);
                  });
                }catch(e){ console.error(e); }
              }

              function exportTickets(){ try{ const raw = localStorage.getItem('raffleTickets')||'[]'; const blob = new Blob([raw],{type:'application/json'}); const url=URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url; a.download='raffleTickets_'+Date.now()+'.json'; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);}catch(e){console.error(e);} }
              function clearTickets(){ if(!confirm('Clear all raffle tickets?')) return; localStorage.removeItem('raffleTickets'); loadTickets(); $('winner').textContent='Tickets cleared.' }

              let lastSelected = -1;
              async function spinAndDraw(){ try{
                const raw = localStorage.getItem('raffleTickets')||'[]'; const arr = JSON.parse(raw); if(!arr.length){ alert('No tickets to draw'); return; }
                const wheel = $('wheel'); const chips = Array.from(wheel.children);
                const cycles = 40 + Math.floor(Math.random()*40); let current = Math.floor(Math.random()*arr.length);
                for(let i=0;i<cycles;i++){
                  current = (current+1)%arr.length; highlight(chips,current);
                  await new Promise(r=>setTimeout(r, 30 + Math.floor((i/cycles)*220)));
                }
                // final slow spins
                for(let j=0;j<8;j++){ current=(current+1)%arr.length; highlight(chips,current); await new Promise(r=>setTimeout(r, 200 + j*80)); }
                const selected = current % arr.length; const winner = arr[selected]; const winnersRaw = localStorage.getItem('raffleWinners')||'[]'; const winners = JSON.parse(winnersRaw); winners.push({...winner, drawnAt:new Date().toISOString()}); localStorage.setItem('raffleWinners', JSON.stringify(winners)); $('winner').textContent = 'Winner: '+winner.tokenId+' — '+(winner.owner||'Unknown'); lastSelected = selected; loadTickets();
                // Auto-request mint: if a recipient address is provided in the input for the winner, post it to opener
                try {
                  const addrEl = document.getElementById('addr_'+selected);
                  const to = addrEl && addrEl.value && addrEl.value.trim() ? addrEl.value.trim() : null;
                  if (window.opener && typeof window.opener.postMessage === 'function') {
                    window.opener.postMessage({ type: 'mint-ticket', tokenId: winner.tokenId, to }, '*');
                  }
                } catch (e) { /* ignore */ }
              }catch(e){console.error(e);} }

              function highlight(chips, idx){ chips.forEach((c,i)=>{ c.classList.toggle('highlight', i===idx); }); const el = chips[idx]; if(el) el.scrollIntoView({behavior:'smooth',inline:'center'}); }

              // wire buttons
              $('closeBtn').onclick = ()=>window.close();
              $('exportBtn').onclick = ()=>exportTickets();
              $('clearBtn').onclick = ()=>clearTickets();
              $('loadOrdersBtn').onclick = async ()=>{
                const target = document.getElementById('ordersList');
                target.textContent = 'Loading orders...';
                // Candidate endpoints (tries relative, opener origin, current origin, localhost, prod)
                const endpoints = [];
                try { endpoints.push('/api/orders'); } catch (e) {}
                try { if (window.opener && window.opener.location && window.opener.location.origin) endpoints.push(window.opener.location.origin + '/api/orders'); } catch (e) {}
                try { endpoints.push(window.location.origin + '/api/orders'); } catch (e) {}
                endpoints.push('http://localhost:4008/api/orders');
                endpoints.push('https://braga.maplusvision.com/api/orders');

                let json = null;
                for (const ep of endpoints) {
                  try {
                    const controller = new AbortController();
                    const id = setTimeout(() => controller.abort(), 5000);
                    const resp = await fetch(ep, { signal: controller.signal });
                    clearTimeout(id);
                    if (!resp) continue;
                    const j = await resp.json().catch(() => null);
                    if (!j) continue;
                    json = j;
                    target.setAttribute('data-source', ep);
                    break;
                  } catch (err) {
                    // try next
                    continue;
                  }
                }
                
                // If direct fetch failed, try asking the opener via postMessage (useful for webviews/popup CORS issues)
                if (!json && window.opener && typeof window.opener.postMessage === 'function') {
                  try {
                    const requestId = 'req_' + Date.now() + '_' + Math.random().toString(36).slice(2,8);
                    json = await new Promise((resolve) => {
                      const timer = setTimeout(() => { window.removeEventListener('message', listener); resolve(null); }, 5000);
                      function listener(ev) {
                        try {
                          const d = ev.data || {};
                          if (!d) return;
                          if (d.type === 'responseOrders' && d.requestId === requestId) {
                            clearTimeout(timer);
                            window.removeEventListener('message', listener);
                            resolve(d.orders || null);
                          }
                        } catch (e) {}
                      }
                      window.addEventListener('message', listener);
                      try {
                        window.opener.postMessage({ type: 'requestOrders', requestId }, '*');
                      } catch (e) {
                        clearTimeout(timer);
                        window.removeEventListener('message', listener);
                        resolve(null);
                      }
                    });
                    if (json) target.setAttribute('data-source', 'opener:postMessage');
                  } catch (e) {
                    // ignore
                  }
                }

                if (!json) { target.textContent = 'No response from server from any endpoint.'; return; }
                const arr = Array.isArray(json) ? json : (json.orders || json.data || []);
                if (!arr || !arr.length) { target.textContent = 'No orders found on server.'; return; }
                target.innerHTML = '';
                arr.slice(0,50).forEach(o=>{
                  const d = document.createElement('div');
                  d.style.padding='8px';
                  d.style.borderBottom='1px solid rgba(255,255,255,0.02)';
                  const tx = o.txHash || (o.meta && o.meta.txHash) || o.transactionHash || '';
                  const buyer = (o.buyer && (o.buyer.firstName || o.buyer.lastName || o.buyer.from)) || o.from || o.buyer || '';
                  const header = '<div style="font-weight:800">' + (o.id || tx || 'order') + ' • ' + (o.method || o.payment || '') + '</div>';
                  const buyerHtml = '<div style="font-size:12px;color:#9fbbe6">Buyer: ' + (typeof buyer === 'string' ? buyer : (buyer.firstName ? (buyer.firstName + ' ' + (buyer.lastName || '')) : JSON.stringify(buyer))) + '</div>';
                  const txHtml = tx ? ('<div style="margin-top:6px;font-size:12px"><a href="https://etherscan.io/tx/' + tx + '" target="_blank">' + tx + '</a></div>') : '';
                  d.innerHTML = header + buyerHtml + txHtml;
                  target.appendChild(d);
                });
              };

              $('checkTxBtn').onclick = ()=>{
                // quick helper to open etherscan for currently selected winner if exists
                try{
                  const winnerEl = document.getElementById('winner');
                  const txt = (winnerEl && winnerEl.textContent) || '';
                  const m = txt.match(/(0x[a-f0-9]{60,})/i);
                  if(m && m[1]) window.open('https://etherscan.io/tx/' + m[1], '_blank'); else alert('No tx found in winner display');
                }catch(e){ alert('No transaction to check'); }
              };
              $('spinBtn').onclick = ()=>spinAndDraw();
              $('mintBtn').onclick = ()=>{ if(!lastSelected){ alert('No winner selected yet'); return; } const chips = Array.from(document.getElementById('wheel').children); const sel = chips[lastSelected]; if(!sel) return; const tokenId = sel.querySelector('.id')?.textContent || null; if(tokenId && window.opener){ window.opener.postMessage({ type:'mint-ticket', tokenId }, '*'); } };
              $('copyBtn').onclick = ()=>{ const txt = $('winner').textContent || ''; if(navigator.clipboard) navigator.clipboard.writeText(txt); alert('Winner copied to clipboard'); };

              // initial load
              loadTickets();
              // listen for storage events to update dynamically when other windows modify tickets
              window.addEventListener('storage', (e)=>{ if(e.key==='raffleTickets') loadTickets(); });
            })();
          <\/script>
        </body></html>
      `;

      popup.document.open();
      popup.document.write(html);
      popup.document.close();
      popup.focus();
    } catch (e) {
      console.warn('openAdminPopup failed', e);
      alert('Could not open admin popup — check console for errors.');
    }
  };

  // Keep receipt in sync with localStorage changes (so ControlCenter updates reflect in kiosk receipts)
  useEffect(() => {
    const handler = (ev) => {
      if (ev.key !== 'store_orders') return;
      try {
        const arr = JSON.parse(ev.newValue || '[]');
        if (!receipt || !receipt.id) return;
        const found = arr.find(a => a.id === receipt.id);
        if (found) {
          setReceipt(prev => ({ ...prev, ...found }));
        }
      } catch (e) {
        // ignore malformed
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [receipt]);

  // Listen for postMessage from popup admin window requesting actions (e.g., mint a ticket)
  useEffect(() => {
    const handler = (ev) => {
      try {
        const msg = ev.data || {};
        if (!msg || !msg.type) return;
        if (msg.type === 'mint-ticket' && msg.tokenId) {
          // find the ticket and call admin mint with optional recipient
          try {
            const raw = localStorage.getItem('raffleTickets') || '[]';
            const arr = JSON.parse(raw);
            const t = arr.find(x => x.tokenId === msg.tokenId);
            if (t) {
              if (msg.to && typeof msg.to === 'string') {
                // try server-side mint endpoint first
                try {
                  (async () => {
                    try {
                      const resp = await apiFetch('/api/raffle/mint', { method: 'POST', body: JSON.stringify({ ticketId: msg.tokenId, to: msg.to }) });
                      const j = await resp.json().catch(() => ({}));
                      if (resp.ok) {
                        // mark ticket locally as minted
                        try {
                          const latest = JSON.parse(localStorage.getItem('raffleTickets') || '[]');
                          const idx = latest.findIndex(r => r.tokenId === msg.tokenId);
                          if (idx >= 0) {
                            latest[idx].status = 'minted';
                            latest[idx].mintedTo = msg.to;
                            if (j.txHash) latest[idx].mintedTxHash = j.txHash;
                            latest[idx].mintedAt = new Date().toISOString();
                            localStorage.setItem('raffleTickets', JSON.stringify(latest));
                            setRaffleTickets(latest);
                          }
                        } catch (_) {}
                        // reply to popup
                        if (ev.source && typeof ev.source.postMessage === 'function') ev.source.postMessage({ type: 'mint-request-accepted', tokenId: msg.tokenId, txHash: j.txHash || null }, '*');
                        return;
                      }
                    } catch (err) {
                      // server mint failed or endpoint missing — fallback to client mint
                    }
                    // fallback to client-side admin mint
                    try { await adminMintToAddress(t, msg.to); } catch (e) { await adminMintWinnerNFT(t); }
                  })();
                } catch (e) {
                  // fallback: mint to connected wallet
                  adminMintWinnerNFT(t);
                }
              } else {
                // no explicit recipient — mint to connected admin wallet in-browser
                adminMintWinnerNFT(t);
              }
              // optionally reply to popup
              if (ev.source && typeof ev.source.postMessage === 'function') {
                ev.source.postMessage({ type: 'mint-request-accepted', tokenId: msg.tokenId }, '*');
              }
            } else {
              if (ev.source && typeof ev.source.postMessage === 'function') {
                ev.source.postMessage({ type: 'mint-request-not-found', tokenId: msg.tokenId }, '*');
              }
            }
          } catch (e) {
            // swallow
          }
        }
        // Support popup asking the opener to fetch /api/orders (postMessage fallback for webviews)
        else if (msg.type === 'requestOrders' && msg.requestId) {
          (async () => {
            try {
              const resp = await apiFetch('/api/orders');
              const json = await resp.json().catch(() => null);
              const arr = Array.isArray(json) ? json : (json && json.orders) ? json.orders : (json && json.data) ? json.data : [];
              if (ev.source && typeof ev.source.postMessage === 'function') {
                ev.source.postMessage({ type: 'responseOrders', requestId: msg.requestId, orders: arr }, '*');
              }
            } catch (e) {
              if (ev.source && typeof ev.source.postMessage === 'function') {
                ev.source.postMessage({ type: 'responseOrders', requestId: msg.requestId, orders: null, error: String(e) }, '*');
              }
            }
          })();
        }
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // On mount, try to fetch server orders and merge into localStorage.store_orders so UI components pick them up
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const resp = await apiFetch('/api/orders');
        const json = await resp.json().catch(() => null);
        const arr = Array.isArray(json) ? json : (json && json.orders) ? json.orders : (json && json.data) ? json.data : [];
        if (!arr || !arr.length) return;
        try {
          const raw = localStorage.getItem('store_orders') || '[]';
          const cur = JSON.parse(raw);
          const merged = Array.isArray(cur) ? [...cur] : [];
          for (const o of arr) {
            try {
              const id = o.id || o.txHash || (`order_srv_${Date.now()}_${Math.random().toString(36).slice(2,6)}`);
              // normalize buyer address into o.buyer.from
              if (o.buyer) {
                const cand = o.buyer.from || o.buyer.contact || o.buyer.address || o.buyer.wallet || null;
                if (cand && typeof cand === 'string') {
                  o.buyer.from = cand.trim().toLowerCase();
                }
              }
              // skip if already present by id or txHash
              const exists = merged.find(x => x.id === o.id || (o.txHash && x.txHash === o.txHash));
              if (!exists) {
                const toSave = { id, created: o.createdAt || new Date().toISOString(), ...o };
                merged.push(toSave);
                try { window.dispatchEvent(new CustomEvent('store:orderCreated', { detail: toSave })); } catch (e) {}
              }
            } catch (e) {
              // continue
            }
          }
          localStorage.setItem('store_orders', JSON.stringify(merged));
        } catch (e) {
          // ignore localStorage write failures
        }
      } catch (e) {
        // ignore fetch errors
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Cleanup BF page background/class when component unmounts to avoid leaking DOM
  useEffect(() => {
    return () => {
      try {
        document.body.classList.remove('bf-page-active');
        const existing = document.querySelector('.bf-page-bg');
        if (existing) existing.remove();
      } catch (e) {
        // ignore
      }
    };
  }, []);

  // Attempt to claim/mint an NFT for a receipt item.
  // Strategy: try server endpoint first (/api/orders/claim) and if not available
  // fall back to a manual instruction that points to the product metadata URL.
  const attemptClaim = async (r, item) => {
    const key = `${r.id}::${item.id}`;
    setClaimStatuses(s => ({ ...s, [key]: { status: 'starting', message: 'Attempting to claim...' } }));
    // find product metadata URL if available
    const product = products.find(p => p.id === item.id) || {};
    const metaUrl = product?.ipfs?.gatewayUrl || (product?.ipfs?.ipfsHash ? `https://gateway.pinata.cloud/ipfs/${product.ipfs.ipfsHash}` : null);
    try {
      // try server-side mint endpoint (expected to be provided by backend)
      const res = await apiFetch('/api/orders/claim', {
        method: 'POST',
        body: JSON.stringify({ orderId: r.id, itemId: item.id })
      });
      if (!res) throw new Error('no-response');
      const json = await res.json().catch(() => ({}));
      if (res.ok) {
        setClaimStatuses(s => ({ ...s, [key]: { status: 'ok', message: json.message || 'Claim initiated; check your wallet.' } }));
        return;
      }
      // non-OK response -> treat as fallback
      setClaimStatuses(s => ({ ...s, [key]: { status: 'fallback', message: json.message || 'Server cannot claim; see manual instructions below.' } }));
    } catch (err) {
      // network or endpoint missing: instruct user to mint manually
      setClaimStatuses(s => ({ ...s, [key]: { status: 'no-server', message: 'No server mint endpoint available; you can mint manually using the metadata URL.' } }));
    }

    // If we have a metadata URL, offer to copy it for manual minting workflows
    if (metaUrl) {
      const copied = await copyToClipboard(metaUrl);
      setClaimStatuses(s => ({ ...s, [key]: { ...(s[key]||{}), metaUrl, copied } }));
    } else {
      setClaimStatuses(s => ({ ...s, [key]: { ...(s[key]||{}), metaUrl: null, copied: false } }));
    }
  };

  // Client-side mint using buyer's wallet (MetaMask) via ethers.
  const clientMint = async (r, item) => {
    const key = `${r.id}::${item.id}`;
    setClaimStatuses(s => ({ ...s, [key]: { status: 'starting', message: 'Attempting wallet mint...' } }));

    if (!detectMetaMask()) {
      setClaimStatuses(s => ({ ...s, [key]: { status: 'no-metamask', message: 'MetaMask not detected in browser' } }));
      return;
    }

    // get tokenURI from product
    const prod = products.find(p => p.id === item.id) || {};
    const tokenURI = prod?.ipfs?.gatewayUrl || (prod?.ipfs?.ipfsHash ? `https://gateway.pinata.cloud/ipfs/${prod.ipfs.ipfsHash}` : null) || r?.nft?.tokenURI || null;
    if (!tokenURI) {
      setClaimStatuses(s => ({ ...s, [key]: { status: 'no-meta', message: 'No metadata URI found for this product' } }));
      return;
    }

    try {
      // Get contract info from server (ABI + address) to avoid embedding ABI in client
      const ci = await apiFetch('/api/contract-info');
      const ciJson = await ci.json().catch(() => ({}));
      const address = ciJson.address || prod?.contractAddress || null;
      const abi = ciJson.abi || prod?.contractAbi || null;
      if (!address || !abi) {
        setClaimStatuses(s => ({ ...s, [key]: { status: 'no-config', message: 'Contract ABI/address not available on server or product. Cannot perform client-side mint.' } }));
        return;
      }

      const provider = new BrowserProvider(window.ethereum);
      await provider.send('eth_requestAccounts', []);
      const signer = await provider.getSigner();
      const userAddr = await signer.getAddress();

      const contract = new Contract(address, abi, signer);

      const tryMethods = [
        { name: 'mintTo', args: (to, uri) => [to, uri] },
        { name: 'safeMint', args: (to, uri) => [to, uri] },
        { name: 'mint', args: (to, uri) => [to, uri] },
        { name: 'claim', args: (to, uri) => [to, uri] },
        { name: 'mintToAddress', args: (to, uri) => [to, uri] },
        { name: 'mintTokenURI', args: (to, uri) => [uri] },
        { name: 'mintWithTokenURI', args: (to, uri) => [uri] },
      ];

      for (const m of tryMethods) {
        if (typeof contract[m.name] !== 'function') continue;
        try {
          const args = m.args(userAddr, tokenURI);
          const tx = await contract[m.name](...args);
          setClaimStatuses(s => ({ ...s, [key]: { status: 'pending', message: `Transaction sent: ${tx.hash}` } }));
          try {
            const receipt = await tx.wait();
            setClaimStatuses(s => ({ ...s, [key]: { status: 'ok', message: `Minted in wallet (tx: ${tx.hash})`, txHash: tx.hash } }));
            return;
          } catch (waitErr) {
            setClaimStatuses(s => ({ ...s, [key]: { status: 'failed', message: `Tx failed: ${waitErr.message || waitErr}` } }));
            return;
          }
        } catch (err) {
          // try next method
          continue;
        }
      }

      setClaimStatuses(s => ({ ...s, [key]: { status: 'failed', message: 'No compatible mint method found on contract' } }));
    } catch (err) {
      setClaimStatuses(s => ({ ...s, [key]: { status: 'failed', message: String(err.message || err) } }));
    }
  };

  function downloadReceipt(r) {
    try {
      const data = r || receipt;
      if (!data) return;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.id || 'receipt'}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // ignore
    }
  }

  async function payWithUsd() {
    // Simulate USD payment: persist order and show instructions.
    if (totalUsd <= 0) {
      setStatus('empty-cart');
      return;
    }
    // create order on server if available
    try {
      const res = await apiFetch('/api/orders', {
        method: 'POST',
        body: JSON.stringify({ items: items.map(it => ({ id: it.id, name: it.name, qty: it.qty, price: it.priceUsd, size: it.size, category: it.category })), total: totalUsd, method: 'usd' })
      });
      const json = await res.json().catch(() => null);
      if (json && Array.isArray(json.products)) setProducts(json.products);
      if (json && json.order && json.order.id) {
        const ord = { id: json.order.id, method: 'usd', totalUsd, items };
        persistOrder(ord);
        processRaffleTickets(ord);
      } else {
        const ord = { method: 'usd', totalUsd, items };
        persistOrder(ord);
        processRaffleTickets(ord);
      }
    } catch (_) {
      // continue to local persist if server call failed
      persistOrder({ method: 'usd', totalUsd, items });
    }
    setStatus('usd-saved');
    setCart([]);
  }

  // Save order locally/server without initiating a payment (used by mobile "Place Order (save only)" flow)
  function placeOrderSaveOnly() {
    if (!firstName.trim() || !lastName.trim()) { setStatus('missing-name'); return; }
    const offOrder = { method: 'offline', totalUsd, items, buyer: { firstName: firstName.trim(), lastName: lastName.trim(), from: buyerWallet || null } };
    try {
      persistOrder(offOrder);
      processRaffleTickets(offOrder);
    } catch (e) {
      console.warn('placeOrderSaveOnly failed', e);
    }
    setCart([]);
    setFirstName(''); setLastName(''); setBuyerWallet('');
    setStatus('saved-offline');
    setShowMobileCheckout(false);
  }

  // Promo purchase helpers (Black Friday bundle) - price constant moved closer to UI

  // Purchase promo using MetaMask (sends ETH to ADMIN_WALLET)
  async function purchasePromoWithMetaMask() {
    if (promoProcessing) return;
    setPromoProcessing(true);
    try {
      if (!detectMetaMask()) {
        alert('MetaMask not detected in this browser.');
        setPromoProcessing(false);
        return;
      }
      if (!promoFirstName.trim() || !promoLastName.trim()) {
        alert('Please enter first and last name to continue.');
        setPromoProcessing(false);
        return;
      }
      if (!ethPriceUsd || ethPriceUsd === 0) {
        alert('ETH price unavailable. Try again later.');
        setPromoProcessing(false);
        return;
      }

      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const from = accounts && accounts[0];
      if (!from) {
        setPromoProcessing(false);
        return;
      }

      const ethAmount = Number((PROMO_PRICE_USD / ethPriceUsd).toFixed(6));

      const ethToWeiHex = (amt) => {
        const s = String(amt || '0');
        const [intPart, fracPart = ''] = s.split('.');
        const intBig = BigInt(intPart || '0') * 10n ** 18n;
        const fracPadded = (fracPart + '0'.repeat(18)).slice(0, 18);
        const fracBig = BigInt(fracPadded || '0');
        const wei = intBig + fracBig;
        return '0x' + wei.toString(16);
      };

      const txParams = {
        from,
        to: ADMIN_WALLET,
        value: ethToWeiHex(ethAmount),
        data: '0x'
      };
      setStatus('promo-sending');
      const txHashResp = await window.ethereum.request({ method: 'eth_sendTransaction', params: [txParams] });
      setStatus('promo-pending');

      const savedOrder = {
        id: `promo_${Date.now()}`,
        method: 'metamask',
        totalUsd: PROMO_PRICE_USD,
        totalEth: ethAmount,
        txHash: txHashResp,
        items: [{ id: 'blackfriday_promo', name: 'Black Friday Promo Bundle', qty: 1, priceUsd: PROMO_PRICE_USD }],
        buyer: { firstName: promoFirstName.trim(), lastName: promoLastName.trim(), contact: promoContact || null, from }
      };
      // Attempt to create order on server; fallback to local persist
      try {
        const res = await apiFetch('/api/orders', { method: 'POST', body: JSON.stringify({ items: savedOrder.items, total: savedOrder.totalUsd, method: savedOrder.method, buyer: savedOrder.buyer }) });
        const json = await res.json().catch(() => null);
        if (res && res.ok && json && json.order) {
          persistOrder(json.order);
          setReceipt({ id: json.order.id, createdAt: new Date().toISOString(), ...json.order });
        } else {
          persistOrder(savedOrder);
          setReceipt({ id: savedOrder.id, createdAt: new Date().toISOString(), ...savedOrder });
        }
      } catch (e) {
        persistOrder(savedOrder);
        setReceipt({ id: savedOrder.id, createdAt: new Date().toISOString(), ...savedOrder });
      }
      setReceipt({ id: savedOrder.id, createdAt: new Date().toISOString(), ...savedOrder });
      setStatus('promo-success');
      setPromoProcessing(false);
      setShowPromoModal(false);
      // clear promo fields
      setPromoFirstName(''); setPromoLastName(''); setPromoContact('');
    } catch (err) {
      console.error('promo purchase failed', err);
      setStatus('promo-failed');
      setPromoProcessing(false);
    }
  }

  // Save promo as USD (local persist) without MetaMask
  function purchasePromoSaveUsd() {
    try {
      const savedOrder = { id: `promo_local_${Date.now()}`, method: 'usd', totalUsd: PROMO_PRICE_USD, items: [{ id: 'blackfriday_promo', name: 'Black Friday Promo Bundle', qty: 1, priceUsd: PROMO_PRICE_USD }], buyer: { firstName: promoFirstName.trim(), lastName: promoLastName.trim(), contact: promoContact || null } };
      // Try to save to server orders endpoint; if that fails, persist locally
      (async () => {
        try {
          const res = await apiFetch('/api/orders', { method: 'POST', body: JSON.stringify({ items: savedOrder.items, total: savedOrder.totalUsd, method: savedOrder.method, buyer: savedOrder.buyer }) });
          const json = await res.json().catch(() => null);
          if (res && res.ok && json && json.order) {
            persistOrder(json.order);
          } else {
            persistOrder(savedOrder);
          }
        } catch (e) {
          persistOrder(savedOrder);
        }
      })();
      setStatus('promo-saved');
      setShowPromoModal(false);
      setPromoFirstName(''); setPromoLastName(''); setPromoContact('');
    } catch (e) {
      setStatus('promo-save-failed');
    }
  }

  // Export promo link or JSON for sharing
  

  // Black Friday promo price (USD) — placed just above the UI for easy mapping
  const PROMO_PRICE_USD = 49.99;

  return (
    <div className="store-wrap">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <h2 className="store-header" style={{ margin: 0 }}>Braga BJJ — Store</h2>
        {/* Black Friday tab: show thumbnails (raffle image + mysterious box pin) and concise CTA */}
        {(() => {
          const raffle = products.find(p => p.id === 'blackfriday_raffle');
          if (!raffle) return null;
          // Use the canonical mysterious box image hosted on Pinata for the thumbnail
          const thumb = raffle.ipfs?.gatewayUrl || `${pinata.pinataGateway}ipfs/bafybeietsxweg65mhwoe25bpc3k3tkpb5gm7e63hbc32izv4e3azpi5hhe`;
          return (
            <div style={{ marginLeft: 12 }}>
              {/* Toggle BF page active class instead of navigating away */}
              <button
                type="button"
                className="blackfriday-tab"
                onClick={(e) => {
                  e.preventDefault();
                  // toggle page-level glassmorph effect
                  try {
                    const isActive = document.body.classList.toggle('bf-page-active');
                    // if enabling, ensure background element exists
                    if (isActive && !document.querySelector('.bf-page-bg')) {
                      const bg = document.createElement('div');
                      bg.className = 'bf-page-bg';
                      bg.setAttribute('aria-hidden', 'true');
                      document.body.appendChild(bg);
                    }
                    // if disabling, remove background element to avoid leftover DOM
                    if (!isActive) {
                      const existing = document.querySelector('.bf-page-bg');
                      if (existing) existing.remove();
                    }
                  } catch (err) {
                    // ignore DOM exceptions
                  }
                }}
                aria-label="Toggle Black Friday view"
              >
                <span className="bf-glow" aria-hidden="true" />
                <div className="bf-thumb-wrap">
                  <img className="bf-thumb" src={thumb} alt="mystery box" />
                  <div className="bf-thumb-film" aria-hidden="true" />
                  <div className="bf-thumb-badge" aria-hidden="true">MYSTERY</div>
                </div>
                <div style={{ marginLeft: 12, minWidth: 190 }}>
                  <div className="bf-text">
                    <span className="bf-title">Black Friday — Mysterious Box (Raffle)</span>
                    <span className="bf-sub">Ticket {fmtUsd(raffle.priceUsd || 20)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                    <div className="bf-prize">Prize: {fmtUsd(raffle.prizeUsd || 199)}</div>
                    <div className="bf-price">{fmtUsd(raffle.priceUsd || 20)}</div>
                    <div style={{ color: '#ffdede', fontSize: 12, marginLeft: 8 }}>Stock: {(raffle.stock != null) ? raffle.stock : 999}</div>
                  </div>
                  <div className="raffle-tooltip" style={{ marginTop: 8 }}>Buy this raffle ticket ({fmtUsd(raffle.priceUsd || 20)}) and at least <strong>{raffle.minItemsRequired || 3}</strong> qualifying items in the same order to be eligible.</div>
                </div>
              </button>
            </div>
          );
        })()}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            className="store-btn ghost"
            onClick={async () => {
              try {
                const url = 'https://braga.maplusvision.com/?bf=1';
                await navigator.clipboard.writeText(url);
                alert('Black Friday link copied: ' + url);
              } catch (e) {
                prompt('Copy this link to share:', 'https://braga.maplusvision.com/?bf=1');
              }
            }}
          >
            Share BF Link
          </button>
          
          <button className="store-btn ghost" onClick={() => openAdminPopup()} title="Open Admin Raffle in a separate popup window">Admin: Popup</button>
        </div>
      </div>
      
      

      <p style={{ color: '#b8c2d1', marginTop: 6 }}>New customers welcome — no PIN or wallet required to browse.</p>

      {products.find(p => p.id === 'blackfriday_raffle') && (
        <div style={{ marginTop: 10 }}>
          <div className="raffle-summary">
            <strong>Black Friday Raffle:</strong> <span style={{ marginLeft: 8 }}>{fmtUsd(20)} ticket — <em>hover for details</em></span>
            <div className="raffle-tooltip">
              <div style={{ marginBottom: 6 }}><strong>Prize:</strong> {fmtUsd(199)}</div>
              <div style={{ marginBottom: 6 }}>Buy the Mysterious Box ticket and at least <strong>3</strong> qualifying items (rashguard, T-Shirt, private coaching, or gi) in the same order to be eligible.</div>
              <div style={{ fontSize: 13, color: '#cce6ff' }}>Tickets are issued as local raffle tokens stored in <code>localStorage.raffleTickets</code>. For NFT-backed tickets, enable server-side minting and IPFS metadata.</div>
            </div>
          </div>
        </div>
      )}
      {/* NEW MEMBERS tab: opens Black Friday promo modal for new members */}
          <button
            type="button"
            className="blackfriday-tab new-members-tab"
            onClick={(e) => { e.preventDefault(); setShowPromoModal(true); }}
            aria-label="New Members — Black Friday Promo"
            style={{ marginLeft: 10 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 100, height: 100, borderRadius: 8, overflow: 'hidden' }}>
                <img src={`${pinata.pinataGateway}ipfs/bafybeietsxweg65mhwoe25bpc3k3tkpb5gm7e63hbc32izv4e3azpi5hhe`} alt="new-members" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ minWidth: 280 }}>
                <div style={{ fontWeight: 800 }}>NEW MEMBERS</div>
                <div style={{ fontSize: 12, color: '#ffdede' }}>Black Friday Promo — {fmtUsd(PROMO_PRICE_USD)}</div>
              </div>
              <button
                title="Promo details"
                className="store-btn ghost"
                onClick={(ev) => { ev.stopPropagation(); setShowPromoModal(true); }}
                style={{ marginLeft: 8 }}
              >
                Info
              </button>
            </div>
          </button>

      {/* Promo modal (create?promo=blackfriday) - moved below NEW MEMBERS and detached from Mysterious Box UI */}
      {showPromoModal && (
        <div
          className={"admin-overlay" + (isInAppBrowser ? ' mobile-overlay' : '')}
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setShowPromoModal(false); }}
        >
            <div className={"admin-panel store-order-modal" + (isInAppBrowser ? ' mobile' : '')}>
            <div className="store-order-header">
                {isInAppBrowser ? (
                  <button className="store-order-close mobile-close" onClick={() => setShowPromoModal(false)}>Close</button>
                ) : null}
                <h3>Black Friday Promo — Limited Time</h3>
                {!isInAppBrowser ? (<button className="store-order-close" onClick={() => setShowPromoModal(false)}>Close</button>) : null}
            </div>
            <div className="store-order-form promo-grid">
                <div className="promo-left">
                <div style={{ fontWeight: 800, fontSize: 18 }}>Black Friday Promo Bundle</div>
                <div className="promo-price">Price: {fmtUsd(PROMO_PRICE_USD)} — Only until Saturday</div>
                <ul style={{ marginTop: 10, color: '#e6f6ff' }}>
                  <li>30 days unlimited membership</li>
                  <li>Official Braga BJJ T-Shirt</li>
                  <li>One private coaching session</li>
                </ul>
                <div className="promo-desc">New members can connect here; existing members may enter a wallet or email to associate the purchase.</div>
                <div className="promo-inputs">
                  <input className="store-input" placeholder="First name" value={promoFirstName} onChange={(e) => setPromoFirstName(e.target.value)} />
                  <input className="store-input" placeholder="Last name" value={promoLastName} onChange={(e) => setPromoLastName(e.target.value)} />
                </div>
                <div style={{ marginTop: 8 }}>
                  <input className="store-input" placeholder="Email or wallet (optional)" value={promoContact} onChange={(e) => setPromoContact(e.target.value)} />
                </div>
                <div className="promo-actions">
                  <button className="store-btn" onClick={() => purchasePromoWithMetaMask()} disabled={promoProcessing}>{promoProcessing ? 'Processing...' : 'Pay with MetaMask'}</button>
                  <button className="store-btn ghost" onClick={() => purchasePromoSaveUsd()}>Save (USD)</button>
                </div>
              </div>
              <div className="promo-right">
                <img src={`${pinata.pinataGateway}ipfs/bafybeietsxweg65mhwoe25bpc3k3tkpb5gm7e63hbc32izv4e3azpi5hhe`} alt="promo" />
                <div style={{ marginTop: 8, color: '#ffdede', fontWeight: 700 }}>Limited time</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="store-grid">
        {products.map((p) => {
          const price = p.priceUsd ?? p.price ?? 0;
          const outOfStock = typeof p.stock === 'number' && p.stock <= 0;
          const sel = selectedOptions[p.id] || {};
          const selCoach = sel.coach || (p.coaches && p.coaches[0]) || null;
          const selSize = sel.size || (p.id === 'private_coaching' ? '30' : (p.defaultSize || 'M'));
          const qtyForSel = cart.find(i => i.id === p.id && i.size === selSize && i.category === (sel.category || 'adult') && (i.coach || null) === selCoach)?.qty || 0;
          // sizes fallback
          // For coaching product we treat size as duration (30 or 60 minutes)
          const sizes = p.id === 'private_coaching' ? (p.durations || ['30','60']) : (p.sizes || ['S', 'M', 'L', 'XL']);
          return (
            <div key={p.id} data-product-id={p.id} className={`store-card store-product ${p.category === 'raffle' ? 'raffle-card' : ''} ${highlightedProductId === p.id ? 'highlighted-collectable' : ''}`}>
              <div style={{ minWidth: 0 }}>
                <strong>{p.name}</strong>
                <div className="meta">{fmtUsd(price)}</div>
                {p.id === 'blackfriday_raffle' && (
                  <div style={{ marginTop: 8 }}>
                    <div className="raffle-badge-row">
                      {p.gifUrl ? (
                        <img
                          src={p.gifUrl}
                          alt="mystery"
                          className={`raffle-gif glow raffle-gif-small`}
                          style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8 }}
                        />
                      ) : (
                        <div className="mystery-box" aria-hidden="true">
                          <div className="mystery-shine" />
                          <div className="mystery-ribbon" />
                          <div className="mystery-seal">{(p.displayId || String(p.id || '').replace(/_/g, ' ').toUpperCase())}</div>
                        </div>
                      )}
                      <div style={{ marginLeft: 12 }}>
                        <div className="raffle-prize">
                          Prize: {fmtUsd(p.prizeUsd || 199)}
                          <span style={{ marginLeft: 8 }} className="help-icon" tabIndex="0" aria-label="Raffle details">?
                            <div className="help-tooltip">
                              <h4>Raffle rules</h4>
                              <p style={{ marginBottom: 8 }}>Buy this raffle ticket ({fmtUsd(p.priceUsd || 20)}) and at least {p.minItemsRequired || 3} qualifying items (rashguard, T-Shirt, private coaching, or gi) in the same order to be eligible.</p>
                              <ul style={{ margin: '6px 0 8px 18px', color: '#e6f6ff' }}>
                                <li><strong>Ticket price:</strong> {fmtUsd(p.priceUsd || 20)}</li>
                                <li><strong>Prize value:</strong> {fmtUsd(p.prizeUsd || 199)}</li>
                                <li><strong>Eligibility:</strong> {p.minItemsRequired || 3} qualifying items in same order</li>
                                <li><strong>Storage:</strong> local tokens in <code>localStorage.raffleTickets</code></li>
                              </ul>
                              {p?.ipfs?.gatewayUrl && (
                                <div style={{ marginTop: 6 }}><a href={p.ipfs.gatewayUrl} target="_blank" rel="noreferrer" style={{ color: '#9bdcff' }}>View pinned metadata (Pinata)</a></div>
                              )}
                              <p style={{ fontSize: 13, color: '#cfe9ff', marginTop: 8 }}>For production: use server-side minting and verified IPFS metadata for NFT-backed tickets.</p>
                            </div>
                          </span>
                        </div>
                        <div className="raffle-logic">{p.minItemsRequired ? `Buy this raffle ticket (${fmtUsd(p.priceUsd)}) and at least ${p.minItemsRequired} qualifying items in the same order to be eligible.` : `Buy this raffle ticket (${fmtUsd(p.priceUsd)}).`}</div>
                      </div>
                    </div>
                  </div>
                )}
                {typeof p.stock === 'number' && (
                  <div style={{ fontSize: 12, color: outOfStock ? '#ff6b6b' : '#9be79b' }}>{outOfStock ? 'Out of stock' : `Stock: ${p.stock}`}</div>
                )}
                {p?.ipfs?.gatewayUrl && (
                  <div style={{ marginTop: 6, fontSize: 12 }}>
                    <a href={p.ipfs.gatewayUrl} target="_blank" rel="noreferrer" style={{ color: '#9bdcff' }}>View NFT metadata</a>
                  </div>
                )}
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select className="store-input" value={sel.category || 'adult'} onChange={(e) => setSelectedOptions(s => ({ ...s, [p.id]: { ...(s[p.id]||{}), category: e.target.value } }))}>
                    <option value="adult">Adult</option>
                    <option value="kid">Kid</option>
                  </select>
                  {p.id !== 'private_coaching' && (
                    <select className="store-input" value={sel.size || (p.defaultSize || 'M')} onChange={(e) => setSelectedOptions(s => ({ ...s, [p.id]: { ...(s[p.id]||{}), size: e.target.value } }))}>
                      {sizes.map(sz => <option key={sz} value={sz}>{sz}</option>)}
                    </select>
                  )}
                  {/* Coach selector + duration selector for private coaching product */}
                  {p.id === 'private_coaching' && (
                    <>
                      <select className="store-input" value={sel.coach || p.coaches?.[0] || ''} onChange={(e) => setSelectedOptions(s => ({ ...s, [p.id]: { ...(s[p.id]||{}), coach: e.target.value } }))}>
                        {(p.coaches || ['Dave','Stu','Jason']).map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <select className="store-input" value={sel.size || '30'} onChange={(e) => setSelectedOptions(s => ({ ...s, [p.id]: { ...(s[p.id]||{}), size: e.target.value } }))}>
                        {(p.durations || ['30','60']).map(d => <option key={d} value={d}>{d === '60' ? '60 min' : '30 min'}</option>)}
                      </select>
                    </>
                  )}
                </div>
              </div>
              <div className="store-controls">
                <button className="store-btn ghost" onClick={() => remove(p.id)} aria-label={`remove-${p.id}`}>-</button>
                <div>{
                  // For coaching product, qtyForSel represents 30-min blocks; display session count
                  p.id === 'private_coaching' ? (qtyForSel && qtyForSel > 0 ? Math.ceil(qtyForSel / (selSize === '60' ? 2 : 1)) : 0) : qtyForSel
                }</div>
                <button className="store-btn" onClick={() => add(p.id)} aria-label={`add-${p.id}`} disabled={outOfStock}>+</button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="store-total">
        <div>Total (USD)</div>
        <div>{fmtUsd(totalUsd)}</div>
      </div>
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', color: '#b8c2d1' }}>
        <div>Estimated (ETH)</div>
        <div>{loadingPrice ? 'loading...' : totalEth ? `${totalEth} ETH` : '—'}</div>
      </div>

      <div className="store-actions">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input className="store-input" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            <input className="store-input" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            <input className="store-input" placeholder="Wallet (optional 0x...)" value={buyerWallet} onChange={(e) => setBuyerWallet(e.target.value)} />
        </div>
        <button className="store-btn" onClick={payWithUsd}>Save Order (USD)</button>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="store-btn" onClick={payWithMetaMask}>Pay with MetaMask</button>
          <button title="Pay in USD via your wallet (we convert USD -> ETH)" className="store-btn" onClick={payWithMetaMaskUsd}>Pay MetaMask (USD → ETH)</button>
          <button className="store-btn ghost" onClick={() => setShowMetaMaskInfo(s => !s)}>?</button>
        </div>
        <button className="store-btn ghost" onClick={() => placeOrderSaveOnly()}>Place Order (save only)</button>
      </div>
      {/* Mobile compact footer + checkout modal for small viewports / in-app browsers */}
      {isMobileView && (
        <>
          <div style={{ position: 'fixed', left: 12, right: 12, bottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 12px', borderRadius: 12, background: 'linear-gradient(180deg, rgba(6,8,12,0.88), rgba(10,12,18,0.98))', boxShadow: '0 10px 30px rgba(0,0,0,0.6)', zIndex: 9999 }}>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
              <div style={{ fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{items.length ? `${items.length} item${items.length>1?'s':''} • ${fmtUsd(totalUsd)}` : 'Cart empty'}</div>
              <div style={{ fontSize: 12, color: '#b8c2d1' }}>{loadingPrice ? 'eth rate loading...' : (totalEth ? `${totalEth} ETH` : '')}</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button className="store-btn ghost" onClick={() => { setShowMobileCheckout(true); }} style={{ padding: '8px 12px' }}>Checkout</button>
            </div>
          </div>

          {showMobileCheckout && (
            <div className={"admin-overlay"} role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setShowMobileCheckout(false); }}>
              <div className={"admin-panel store-order-modal mobile"} style={{ maxWidth: 520, width: '95%', padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontWeight: 800 }}>Checkout</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="store-btn ghost" onClick={() => setShowMobileCheckout(false)}>Close</button>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input className="store-input" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  <input className="store-input" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  <input className="store-input" placeholder="Wallet (optional 0x...)" value={buyerWallet} onChange={(e) => setBuyerWallet(e.target.value)} />
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                    <button className="store-btn" onClick={payWithUsd} style={{ flex: 1 }}>Save Order (USD)</button>
                    <button className="store-btn" onClick={payWithMetaMask} style={{ flex: 1 }}>{detectMetaMask() ? 'Pay with MetaMask' : 'Pay with Wallet'}</button>
                    <button className="store-btn" onClick={payWithMetaMaskUsd} style={{ flex: 1 }}>Pay MetaMask (USD → ETH)</button>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="store-btn ghost" onClick={() => { placeOrderSaveOnly(); }} style={{ flex: 1 }}>Place Order (save only)</button>
                    <button className="store-btn ghost" onClick={() => { setShowMobileCheckout(false); }}>Done</button>
                  </div>
                  <div style={{ fontSize: 12, color: '#b8c2d1' }}>
                    {detectMetaMask() ? (<span>MetaMask detected</span>) : (<span>No MetaMask detected — use wallet or Save Order (USD)</span>)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {status && (
        <div className="store-status"><strong>Status:</strong> {status}</div>
      )}

      {txHash && (
        <div className="store-status">Transaction: <a href={`https://etherscan.io/tx/${txHash}`} target="_blank" rel="noreferrer">{txHash}</a></div>
      )}

      {receipt && (
        <div className="store-status" style={{ marginTop: 12 }}>
          <strong>Receipt</strong>
          <div>Receipt ID: {receipt.id}</div>
          <div>Created: {receipt.createdAt}</div>
          <div>Buyer: {receipt.buyer?.firstName} {receipt.buyer?.lastName}</div>
          <div style={{ marginTop: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Items</div>
            {receipt.items?.map((it, idx) => {
              const prod = products.find(p => p.id === it.id) || {};
              const metaUrl = prod?.ipfs?.gatewayUrl || (prod?.ipfs?.ipfsHash ? `https://gateway.pinata.cloud/ipfs/${prod.ipfs.ipfsHash}` : null);
              const key = `${receipt.id}::${it.id}`;
              const cs = claimStatuses[key] || {};
              return (
                <div key={`ritem-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    {it.name} x{(it.id === 'private_coaching') ? (Math.ceil((it.qty || 0) / (it.size === '60' ? 2 : 1))) : (it.qty || 0)} {it.size ? `(${it.size === '60' ? '60 min' : it.size === '30' ? '30 min' : it.size})` : ''}
                    {it.coach ? (<span style={{ marginLeft: 8, color: '#c4b5fd', fontWeight: 700 }}>Coach: {it.coach}</span>) : null}
                  </div>
                  {metaUrl && (
                    <a href={metaUrl} target="_blank" rel="noreferrer" style={{ color: '#9bdcff', fontSize: 13 }}>View metadata</a>
                  )}
                  <button className="store-btn" onClick={() => attemptClaim(receipt, it)} style={{ marginLeft: 8 }}>Claim / Mint (Server)</button>
                  <button className="store-btn ghost" onClick={() => clientMint(receipt, it)} style={{ marginLeft: 8 }}>Mint in Wallet</button>
                  {cs && cs.status && (
                    <div style={{ fontSize: 12, color: cs.status === 'ok' ? '#9be79b' : '#f5d36b', marginLeft: 8 }}>{cs.message}{cs.copied ? ' (metadata URL copied)' : ''}</div>
                  )}
                </div>
              );
            })}
          </div>
          <div>Total USD: {fmtUsd(receipt.totalUsd)}</div>
          <div>Total ETH: {receipt.totalEth} ETH</div>
          <div>Tx: {receipt.meta?.txHash ? <a href={`https://etherscan.io/tx/${receipt.meta.txHash}`} target="_blank" rel="noreferrer">{receipt.meta.txHash}</a> : '—'}</div>
          <div style={{ marginTop: 8 }}>
            <button className="store-btn" onClick={() => downloadReceipt()}>Download Receipt</button>
          </div>
        </div>
      )}

      {showAdmin && (
        <div className="admin-overlay" role="dialog" aria-modal="true">
          <div className="admin-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>Raffle Admin</h3>
              <div>
                <button className="store-btn ghost" onClick={() => { setShowAdmin(false); setRaffleWinner(null); }}>Close</button>
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <div style={{ marginBottom: 8 }}>Tickets stored locally: <strong>{raffleTickets.length}</strong></div>
              <div className="admin-list">
                {raffleTickets.length === 0 ? <div style={{ color: '#cbd5e1' }}>No tickets found.</div> : raffleTickets.map((t, i) => (
                  <div key={`ticket-${i}`} style={{ padding: 8, borderBottom: '1px solid rgba(255,255,255,0.02)', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
                      <div style={{ width: 18, height: 18, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: (i === highlightIndex ? '#ff7b7b' : '#2b3340'), boxShadow: (i === highlightIndex ? '0 6px 18px rgba(255,40,80,0.28)' : 'none'), color: '#fff', fontSize: 11 }}>{i+1}</div>
                      <div style={{ overflow: 'hidden', minWidth: 0 }}>
                        <div style={{ fontWeight: 800, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{t.tokenId}</div>
                        <div style={{ color: '#9fbbe6', fontSize: 13, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{t.owner || 'Unknown'} • {t.orderRef || 'no-ref'}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <div style={{ fontSize: 12, color: '#a8c0e6', minWidth: 140 }}>{t.createdAt}</div>
                      <div style={{ fontSize: 12, color: t.status === 'minted' ? '#9be79b' : '#ffdede' }}>{t.status || 'pending'}</div>
                      {t.mintedTo && <div style={{ fontSize: 12, color: '#9fd7b7' }}>to {t.mintedTo}</div>}
                      {t.mintedTxHash && <a style={{ color: '#9bdcff', fontSize: 12 }} href={`https://etherscan.io/tx/${t.mintedTxHash}`} target="_blank" rel="noreferrer">tx</a>}
                      <input
                        className="store-input"
                        placeholder="0x recipient"
                        value={recipientAddresses[t.tokenId] || ''}
                        onChange={(e) => setRecipientAddresses(prev => ({ ...prev, [t.tokenId]: e.target.value }))}
                        style={{ width: 180, padding: '8px 10px' }}
                      />
                      <button className="store-btn" onClick={() => adminMintToAddress(t, recipientAddresses[t.tokenId] || '')}>Send NFT</button>
                    </div>
                  </div>
                ))}
              </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <input className="store-input" placeholder="Search server orders by wallet (0x...)" value={adminWalletSearch} onChange={(e) => setAdminWalletSearch(e.target.value)} style={{ flex: 1 }} />
                  <button className="store-btn" onClick={async () => {
                    try {
                      setServerOrdersLoading(true);
                      const resp = await apiFetch('/api/orders');
                      const json = await resp.json().catch(() => null);
                      const arr = Array.isArray(json) ? json : (json && json.orders) ? json.orders : [];
                      const needle = (adminWalletSearch || '').trim().toLowerCase();
                      if (needle) {
                        const filtered = arr.filter(o => {
                          try {
                            const buyer = o && (o.buyer || o.from || o.meta || {});
                            const candidate = (buyer && (buyer.from || buyer.contact || buyer.address)) || '';
                            return String(candidate).toLowerCase().indexOf(needle) >= 0 || String(o.txHash || o.transactionHash || '').toLowerCase().indexOf(needle) >= 0;
                          } catch (e) { return false; }
                        });
                        setServerOrders(filtered);
                      } else {
                        setServerOrders(arr.slice(0, 200));
                      }
                    } catch (e) {
                      setServerOrders([]);
                    } finally { setServerOrdersLoading(false); }
                  }}>Hard Pull</button>
                  <button className="store-btn ghost" onClick={() => {
                    try {
                      const raw = localStorage.getItem('store_orders') || '[]';
                      const arr = JSON.parse(raw);
                      const needle = (adminWalletSearch || '').trim().toLowerCase();
                      if (!needle) { alert('Enter a wallet or tx hash to search local orders'); return; }
                      const found = arr.filter(o => {
                        try {
                          const candidate = (o.buyer && (o.buyer.from || o.buyer.contact || o.buyer.address)) || '';
                          return String(candidate).toLowerCase().indexOf(needle) >= 0 || String(o.txHash || '').toLowerCase().indexOf(needle) >= 0;
                        } catch (e) { return false; }
                      });
                      if (!found.length) { alert('No local orders matched that wallet/tx'); return; }
                      const summary = found.map(f => `${f.id} • ${f.buyer?.from || f.txHash || ''} • ${f.items?.map(i=>i.id).join(',')}`).join('\n\n');
                      alert('Local orders found:\n\n' + summary);
                    } catch (e) { alert('Search failed: ' + String(e)); }
                  }}>Search Local</button>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Server Orders</strong> {serverOrdersLoading ? '(loading...)' : ''}
                </div>
                <div style={{ maxHeight: 200, overflow: 'auto', padding: 8, borderRadius: 8, background: 'rgba(255,255,255,0.02)' }}>
                  {serverOrders && serverOrders.length ? serverOrders.map((o, idx) => (
                    <div key={`sord-${idx}`} style={{ padding: 8, borderBottom: '1px solid rgba(255,255,255,0.03)', fontSize: 13 }}>
                      <div style={{ fontWeight: 800 }}>{o.id || o.txHash || ('order_' + idx)}</div>
                      <div style={{ color: '#9fbbe6', fontSize: 12 }}>{(o.buyer && (o.buyer.firstName || o.buyer.lastName || o.buyer.from)) || o.from || ''}</div>
                      <div style={{ marginTop: 6 }}>{(o.items || []).map(it => it.id).join(', ')}</div>
                    </div>
                  )) : <div style={{ color: '#cbd5e1' }}>No server orders loaded. Use Hard Pull to fetch.</div>}
                </div>
              </div>
              <div className="admin-actions">
                <button className="store-btn" onClick={async () => { if (isSpinning) return; await spinAndDraw(); }} disabled={isSpinning}>{isSpinning ? 'Spinning...' : 'Spin & Draw'}</button>
                <button className="store-btn ghost" onClick={() => exportRaffleTickets()}>Export Tickets</button>
                <button className="store-btn ghost" onClick={() => { localStorage.removeItem('raffleTickets'); setRaffleTickets([]); }}>Clear Tickets</button>
              </div>
              {raffleWinner && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: 'linear-gradient(90deg,#0f1724,#071028)', border: '1px solid rgba(255,255,255,0.03)' }}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Winner:</strong> {raffleWinner.tokenId} — {raffleWinner.owner}
                    <div style={{ fontSize: 12, color: '#9fd7b7' }}>Drawn: {raffleWinner.drawnAt}</div>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <button className="store-btn" onClick={() => adminMintWinnerNFT(raffleWinner)}>Mint Winner NFT</button>
                    <button className="store-btn ghost" onClick={() => { copyToClipboard(JSON.stringify(raffleWinner)); }}>Copy Winner JSON</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
} 

export default StoreOrder;